import { createContext } from "react";
import { type IObservableProperty, ObservableProperty } from "../observable/observableProperty";
import { ISessionData } from "./v1/interfaces/data/ISessionData";
import { ISessionDataV1 } from "./v1/interfaces/data/ISessionDataV1";
import { SessionV1 } from "./v1/SessionV1";
import { ISessionV1 } from "./v1/interfaces/classes/ISessionV1";
import { IPuzzleV1 } from "./v1/interfaces/classes/IPuzzleV1";
import { areSimpleDatesEqual, compareSimpleDates, SimpleDate } from "./SimpleDate";
import { getSimpleDateInSeattle } from "./getSimpleDateInSeattle";
import { IPuzzleRemoteDataV1 } from "./v1/interfaces/data/IPuzzleRemoteDataV1";
import { IWordInPuzzleV1 } from "./v1/interfaces/classes/IWordInPuzzleV1";
import { IPlayerStats } from "./v1/interfaces/data/IPlayerStats";
import {
  fetchPlayerStats,
  fetchPlayedDates,
  fetchPuzzleForDate,
  pokeRolloverAndRefetch,
  triggerRollover,
  submitPlay,
  submitFeedback,
  toPuzzleId,
  createInvite,
  acceptInvite,
  cancelInvite,
  removeFriend,
  renameFriend,
  setDisplayName,
  fetchSelfSocial,
  fetchRoster,
  fetchInvitePreview,
  fetchLeaderboardEntry,
  fetchPremiumProjection,
  fetchPlayerPlay,
  fetchPremiumStatus,
  storeBillingRequest,
  startPatreonLink,
  finalizePatreonLink,
  unlinkPatreon,
  type FeedbackContext,
  type Roster,
  type LeaderboardEntry,
  type PlayerPlay,
  type InvitePreview,
  fetchOperationsDashboard,
  type OperationsDashboardData,
  type PremiumStatus,
  type PremiumSource,
  type PremiumTier,
  type FetchPuzzleResult,
} from "./backendApi";
import {
  collectFeedbackDiagnostics,
  recordDiagnosticEvent,
  type FeedbackDiagnostics,
} from "../diagnostics";
import {
  authState,
  signOut as signOutAll,
  getSessionToken,
  clearSessionToken,
  getRosterToken,
  setRosterToken,
  setTestSignedIn,
} from "../auth/authState";
import { googleAuth } from "../auth/googleAuth";
import { appleAuth } from "../auth/appleAuth";
import {
  isTestMode,
  getTestScenarios,
  buildTestRoster,
  buildTestLeaderboardEntry,
  buildTestPlayerPlay,
  buildTestPlayerStats,
  buildTestOperationsDashboard,
  TEST_SELF_ID,
} from "../testMode";
import { APP_VERSION } from "../version";
import { PremiumDataGeneration } from "./premiumDataGeneration";
import { withTimeout } from "../helpers/withTimeout";
import { mergePendingPuzzleStats } from "./playerStats";
import { PATREON_JOIN_URL } from "../premiumMembership";
import {
  billingPlugin,
  matchStorePlans,
  nativeStore,
  StorePlan,
  StoreProduct,
  StoreTransaction,
} from "../storeBilling";

export type FeedbackSubmitResult = "Success" | "NotSignedIn" | "RateLimited" | "Error";

const AUTH_REFRESH_TIMEOUT_MS = 20_000;

export type Screen =
  | "Loading"
  | "Connecting"
  | "Home"
  | "Puzzle"
  | "ReviewPuzzle"
  | "SubmittingSolution"
  | "PlayerStats"
  | "Friends"
  | "Leaderboard"
  | "FriendPlay"
  | "Settings"
  | "Operations"
  | "TestScenarios";

/**
 * A full-screen overlay that can sit on top of a screen. Each open overlay is a
 * real browser-history entry (see DataLayer.openOverlay), so a native Back /
 * swipe-back closes it exactly like any other back step.
 */
export type OverlayId = "help" | "feedback" | "rarity";
export interface FeedbackPrefill {
  sentiment: "negative";
  message: string;
}

/**
 * The serialisable snapshot we stash in `history.state` for every navigation
 * entry. The browser is the single source of truth for navigation: on any
 * popstate we render whatever the resulting entry says. `depth` is the entry's
 * distance from the root (0 = root) so we can unwind to the root in one
 * `history.go(-depth)`.
 */
interface NavState {
  screen: Screen;
  depth: number;
  overlay: OverlayId | null;
  /**
   * For puzzle screens (Puzzle/ReviewPuzzle/SubmittingSolution), the date of the
   * puzzle the entry shows. The browser is the source of truth, so a Back that
   * left the puzzle clears `currentPuzzle`; stamping the date here lets a Forward
   * (or any re-entry) restore it via findPuzzleByDate instead of rendering blank.
   */
  puzzleDate?: SimpleDate;
  /** Rejects stale player-specific screens after sign-out or account replacement. */
  sessionGeneration?: number;
}

/** Screens that own `currentPuzzle`; returning to any other screen clears it. */
const PUZZLE_SCREENS = new Set<Screen>(["Puzzle", "ReviewPuzzle", "SubmittingSolution"]);

/**
 * Font used for the letters on the play surface (the puzzle board tiles and the
 * word being traced). Chosen on the Settings screen and persisted locally.
 * - `"serif"` — the default Newsreader display serif (`--font-board-serif`).
 * - `"sans"`  — a plain sans-serif (`--font-board-sans`, Calibri-leading).
 */
export type BoardFont = "serif" | "sans";

const BOARD_FONT_KEY = "WirderBoardFontV1";
const DEFAULT_BOARD_FONT: BoardFont = "serif";

/** Restore the saved board-font preference, defaulting to the classic serif. */
function readSavedBoardFont(): BoardFont {
  try {
    return localStorage.getItem(BOARD_FONT_KEY) === "sans" ? "sans" : DEFAULT_BOARD_FONT;
  } catch {
    return DEFAULT_BOARD_FONT;
  }
}

/** Reflect the board font onto <html> so CSS can map it to `--font-board`. */
function applyBoardFontToDocument(font: BoardFont): void {
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-board-font", font);
  }
}

/**
 * Global text-size preference. The app's text is sized almost entirely in `rem`,
 * so scaling the root font-size proportionally enlarges every text element —
 * including the smallest labels (the found-word chips, stat labels, captions,
 * etc.). Chosen on the Settings screen and persisted locally.
 * - `"default"` — the standard 100% root size.
 * - `"large"`   — 112.5% root size.
 * - `"larger"`  — 125% root size.
 *
 * The puzzle board tiles are sized in `px`/`cqh` (not `rem`), so they stay put
 * and the play layout stays intact while the surrounding text grows.
 */
export type TextSize = "default" | "large" | "larger";

const TEXT_SIZE_KEY = "WirderTextSizeV1";
const DEFAULT_TEXT_SIZE: TextSize = "default";

/** Restore the saved text-size preference, defaulting to the standard size. */
function readSavedTextSize(): TextSize {
  try {
    const saved = localStorage.getItem(TEXT_SIZE_KEY);
    return saved === "large" || saved === "larger" ? saved : DEFAULT_TEXT_SIZE;
  } catch {
    return DEFAULT_TEXT_SIZE;
  }
}

/** Reflect the text size onto <html> so CSS can scale the root font-size. */
function applyTextSizeToDocument(size: TextSize): void {
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-text-size", size);
  }
}

/**
 * Status of today's puzzle on the Home screen:
 * - `"idle"`    — today's puzzle is present (or not applicable); render normally.
 * - `"loading"` — today's puzzle wasn't published yet; we've poked the server to
 *                 run the rollover and are re-checking the CDN. Show a loading card.
 * - `"notReady"`— the poke + retry budget elapsed without the puzzle appearing.
 *                 Show a gentle "not ready, check back shortly" card.
 */
export type TodayPuzzleStatus = "idle" | "loading" | "notReady";

export type OperationsDashboardView =
  | { status: "idle" | "loading" }
  | { status: "loaded"; data: OperationsDashboardData }
  | { status: "unauthorized" | "error" };

/**
 * Local storage key. The "V3" suffix forces a fresh session on first load
 * for users from the previous (UUID-based) phase, since the player ID
 * format has changed.
 */
const LOCAL_DATA_KEY = "WirderLocalDataV3";
const OFFLINE_MODE_KEY = "WirderOfflineMode";

/**
 * Steady-state interval for re-checking a not-yet-published "today" puzzle after
 * the initial poke + fast backoff has been exhausted. Gentle on purpose: the
 * rollover is usually only minutes late, and the server short-circuits cheaply
 * when the puzzle already exists.
 */
const TODAY_POLL_INTERVAL_MS = 20000;

/**
 * Cadence and budget for the background "past puzzle played but not yet scored"
 * poll. A finished puzzle's rare-word bonus is only known once the daily
 * rollover scores this player's play; if the app is open during that window we
 * poll the backend so the review screen fills in the real bonus on its own,
 * rather than making the user reload. Capped so we never poll forever if
 * scoring is genuinely delayed (the value still fills in on the next app open).
 */
const PENDING_SCORE_POLL_INTERVAL_MS = 20000;
const PENDING_SCORE_POLL_MAX_CYCLES = 15;

export type LoadOptions = "OverrideSavedAccountWithNewOne" | "UseSavedAccount";
export type LoadResult =
  | "Success"
  | "NotSignedIn"
  | "Error"
  | "ConfirmUseNewAccountInsteadOfSavedOne";

export type SubmitSolutionResult =
  | "Success"
  | "NotSignedIn"
  | "InvalidDate"
  | "SolutionAlreadySubmitted"
  | "Error";

/** sessionStorage key holding a pending invite code captured from an /i/{code}
 *  deep link, consumed after sign-in on the Friends screen. */
const PENDING_INVITE_KEY = "WirderPendingInviteCodeV1";
const PENDING_PATREON_RESULT_KEY = "WirderPendingPatreonResultV1";
const PENDING_PATREON_PROOF_KEY = "WirderPendingPatreonProofV1";
const PENDING_PATREON_MEMBERSHIP_KEY = "WirderPendingPatreonMembershipV1";
const PATREON_UNIVERSAL_LINK_MARKER = "patreon-callback";
const PATREON_PURCHASE_INTENT_KEY = "WirderPatreonPurchaseIntentV1";
const PREMIUM_ENTITLEMENT_CACHE_KEY = "WirderPremiumEntitlementV2";
const PATREON_PURCHASE_INTENT_MAX_AGE_MS = 2 * 60 * 60 * 1000;
const PREMIUM_RESUME_REFRESH_COOLDOWN_MS = 5000;
const AUTOMATIC_ENTITLEMENT_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const PATREON_PURCHASE_RETRY_DELAYS_MS = [2000, 5000, 10000] as const;

function playerSessionKey(base: string, playerId: string): string {
  return `${base}:${playerId}`;
}

function premiumTierRank(tier: PremiumTier | null): number {
  return tier === "gold" ? 3 : tier === "silver" ? 2 : tier === "bronze" ? 1 : 0;
}

function isPremiumTier(value: unknown): value is PremiumTier {
  return value === "bronze" || value === "silver" || value === "gold";
}

function isPremiumStatus(value: unknown): value is PremiumStatus {
  if (!value || typeof value !== "object") return false;
  const status = value as Partial<PremiumStatus>;
  const patreon = status.patreon as Partial<PremiumStatus["patreon"]> | undefined;
  return (
    (status.tier === null || isPremiumTier(status.tier)) &&
    (status.source === null ||
      status.source === "patreon" ||
      status.source === "complimentary" ||
      status.source === "apple" ||
      status.source === "google") &&
    (!status.subscription ||
      ((status.subscription.store === "apple" || status.subscription.store === "google") &&
        isPremiumTier(status.subscription.tier) &&
        Number.isFinite(Date.parse(status.subscription.expiresAt)))) &&
    ((status.source !== "apple" && status.source !== "google") ||
      status.subscription?.store === status.source) &&
    !!patreon &&
    typeof patreon.configured === "boolean" &&
    typeof patreon.linked === "boolean" &&
    (patreon.tier === null || isPremiumTier(patreon.tier)) &&
    (patreon.displayName === undefined || typeof patreon.displayName === "string") &&
    (patreon.membershipStatus === "active_patron" ||
      patreon.membershipStatus === "declined_patron" ||
      patreon.membershipStatus === "former_patron" ||
      patreon.membershipStatus === "none")
  );
}

function generateBrowserTransactionProof(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

/** One row in the friend leaderboard: a friend (or you) + their score. */
export interface LeaderboardRow {
  playerId: string;
  displayName: string;
  isSelf: boolean;
  tier: PremiumTier | null;
  entry: LeaderboardEntry;
}

/** State of the leaderboard for a single puzzle date. */
export interface LeaderboardView {
  generation: number;
  puzzleId: string;
  loading: boolean;
  scoresVisible: boolean;
  rows: LeaderboardRow[];
  verificationFailed?: boolean;
}

export type PremiumStateStatus = "idle" | "loading" | "ready" | "error";

export interface PremiumState {
  generation: number;
  status: PremiumStateStatus;
  tier: PremiumTier | null;
  source: PremiumSource;
  subscription?: PremiumStatus["subscription"];
  billingEnvironment?: PremiumStatus["billingEnvironment"];
  patreon: PremiumStatus["patreon"];
  players: Record<string, PremiumTier | null>;
  error?: string;
}

export interface PremiumCelebration {
  tier: PremiumTier;
  previousTier: PremiumTier | null;
}

export interface PremiumNotice {
  message: string;
  action?: "join";
}

export type PatreonPurchaseState =
  | "idle"
  | "waiting"
  | "checking"
  | "exhausted"
  | "error"
  | "errorExhausted";
export type PremiumRefreshOutcome = "active" | "noTier" | "error";

const EMPTY_PREMIUM_STATE: PremiumState = {
  generation: 0,
  status: "idle",
  tier: null,
  source: null,
  patreon: {
    configured: false,
    linked: false,
    tier: null,
    membershipStatus: "none",
  },
  players: {},
};

function readPremiumCallback(rawUrl?: string): {
  notice: string | null;
  resultId: string | null;
} {
  if (typeof window === "undefined") {
    return { notice: null, resultId: null };
  }
  let url: URL;
  try {
    url = new URL(rawUrl ?? window.location.href);
  } catch {
    return { notice: null, resultId: null };
  }
  if (url.origin !== window.location.origin) {
    return { notice: null, resultId: null };
  }
  const result = url.searchParams.get("patreon");
  if (!result) return { notice: null, resultId: null };
  const resultId = url.searchParams.get("patreonResult");
  if (!rawUrl) {
    url.searchParams.delete("patreon");
    url.searchParams.delete("patreonResult");
    if (url.searchParams.get("invite") === PATREON_UNIVERSAL_LINK_MARKER) {
      url.searchParams.delete("invite");
    }
    window.history.replaceState(window.history.state, "", url);
  }

  switch (result) {
    case "complete":
      return /^[A-Za-z0-9_-]{32,128}$/.test(resultId ?? "")
        ? { notice: null, resultId }
        : {
            notice: "That Patreon link could not be completed. Please try again.",
            resultId: null,
          };
    case "cancelled":
      return {
        notice: "Patreon linking was cancelled. Nothing changed.",
        resultId: null,
      };
    case "already-linked":
      return {
        notice: "That Patreon account is already linked to another Wirder account.",
        resultId: null,
      };
    case "invalid-state":
      return {
        notice: "That Patreon link expired or was already used. Please try again.",
        resultId: null,
      };
    default:
      return {
        notice: "Patreon linking didn't finish. Please try again.",
        resultId: null,
      };
  }
}

/**
 * State of the "see a friend's found words" view: which player/date is being
 * viewed, plus their loaded play (board + found words). `loading` is true while
 * fetching; `error` is true if the puzzle/play couldn't be loaded.
 */
export interface FriendPlayView {
  playerId: string;
  displayName: string;
  isSelf: boolean;
  date: SimpleDate;
  puzzleId: string;
  ownWords: string[];
  selfPlayed: boolean;
  loading: boolean;
  error: boolean;
  play: PlayerPlay | null;
}

export type FriendActionResult =
  | "Success"
  | "NotSignedIn"
  | "NotFound"
  | "Expired"
  | "AlreadyUsed"
  | "RateLimited"
  | "Error";

/**
 * Rank key for a leaderboard entry. Within a single puzzle date every row is at
 * the same scoring stage (all unscored for today, all scored for prior days), so
 * this stays apples-to-apples: scored rows rank by total points, today's rows by
 * regular points, and anyone who hasn't played sinks to the bottom.
 */
function leaderboardRank(e: LeaderboardEntry): number {
  if (!e.played) return -1;
  if (e.scored && e.totalPoints !== undefined) return e.totalPoints;
  return e.regularPoints;
}

/**
 * Order leaderboard rows best-first, tie-breaking by word count then display
 * name so the ordering is stable. Shared by the live and test-mode paths.
 */
function sortLeaderboardRows(rows: LeaderboardRow[]): void {
  rows.sort((a, b) => {
    const diff = leaderboardRank(b.entry) - leaderboardRank(a.entry);
    if (diff !== 0) return diff;
    if (b.entry.wordCount !== a.entry.wordCount) {
      return b.entry.wordCount - a.entry.wordCount;
    }
    return a.displayName.localeCompare(b.displayName);
  });
}

export class DataLayer {
  private _session: ISessionV1 | undefined;

  /**
   * Generation guard + timer for the background "today puzzle not ready" poll.
   * Bumping the generation invalidates any in-flight or scheduled poll so we
   * never run two loops at once (e.g. reload, manual retry, or sign-out).
   */
  private _todayPollGeneration = 0;
  private _todayPollTimer: ReturnType<typeof setTimeout> | undefined;

  /**
   * Generation guard + timer for the background "past puzzle not yet scored"
   * poll (see PENDING_SCORE_POLL_INTERVAL_MS). Mirrors the today-poll guards so
   * a reload or sign-out cleanly supersedes any in-flight loop.
   */
  private _pendingScorePollGeneration = 0;
  private _pendingScorePollTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly _premiumDataGeneration = new PremiumDataGeneration();
  private _friendPlayLoadGeneration = 0;
  private _sessionGeneration = 0;
  private _pendingPatreonResult: string | null = null;
  private _pendingPatreonProof: string | null = null;
  private _pendingPatreonPlayerId: string | null = null;
  private _pendingPatreonFinalizeRequest: Promise<boolean> | null = null;
  private _testPremiumRefreshSequence: Array<PremiumTier | null | "error"> | undefined;
  private _testPremiumRefreshIndex = 0;
  private _testPremiumRefreshDelayMs = 0;
  private _testPurchaseRetryDelaysMs: number[] | undefined;
  private _testPlayerStats: IPlayerStats | undefined;
  private _premiumRefreshRequest: Promise<boolean> | null = null;
  private _lastPremiumResumeRefreshAt = 0;
  private _patreonPurchaseRetryGeneration = 0;
  private _patreonPurchaseRetryAttempt = 0;
  private _patreonPurchaseRetryTimer: ReturnType<typeof setTimeout> | undefined;
  private _patreonPurchaseRetryScreen: Screen | undefined;
  private _confirmedPaidMembership?: {
    playerId: string;
    environment: NonNullable<PremiumStatus["billingEnvironment"]>;
    tier: PremiumTier | null;
  };

  private readonly _onWindowFocus = () => {
    if (typeof document === "undefined" || document.visibilityState !== "hidden") {
      void this._refreshPremiumAccessOnResume();
    }
  };

  private readonly _onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      void this._refreshPremiumAccessOnResume();
    } else {
      this._cancelPatreonPurchaseRetryTimer();
    }
  };

  private readonly _onPageShow = () => {
    void this._refreshPremiumAccessOnResume();
  };

  private readonly _onScreenChanged = (screen: Screen) => {
    if (this._patreonPurchaseRetryScreen && screen !== this._patreonPurchaseRetryScreen) {
      this._cancelPatreonPurchaseRetryTimer();
    }
  };

  public get puzzles(): IPuzzleV1[] {
    return this._session?.puzzles || [];
  }

  public readonly currentPuzzle: IObservableProperty<IPuzzleV1 | undefined> =
    new ObservableProperty<IPuzzleV1 | undefined>(undefined);

  public readonly currentScreen: IObservableProperty<Screen> = new ObservableProperty<Screen>(
    "Loading",
  );

  public readonly operationsDashboard: IObservableProperty<OperationsDashboardView> =
    new ObservableProperty<OperationsDashboardView>({ status: "idle" });

  private _operationsRouteRequested =
    typeof window !== "undefined" && window.location.pathname === "/operations";

  public consumeOperationsRouteRequest(): boolean {
    const requested = this._operationsRouteRequested;
    this._operationsRouteRequested = false;
    return requested;
  }

  /**
   * The full-screen overlay currently open on top of `currentScreen` (Help,
   * Feedback, or the review rarity explainer), or `undefined` when
   * none is open. Overlays are real browser-history entries (see openOverlay),
   * so a native Back / swipe-back closes them like any other back step.
   */
  public readonly currentOverlay: IObservableProperty<OverlayId | undefined> =
    new ObservableProperty<OverlayId | undefined>(undefined);

  public readonly feedbackPrefill: IObservableProperty<FeedbackPrefill | undefined> =
    new ObservableProperty<FeedbackPrefill | undefined>(undefined);

  /**
   * The player's chosen play-surface font. Persisted to localStorage and
   * mirrored onto <html>'s `data-board-font` so the board CSS can switch
   * `--font-board`. Change it via `setBoardFont` (keeps storage + DOM in sync).
   */
  public readonly boardFont: IObservableProperty<BoardFont> = new ObservableProperty<BoardFont>(
    readSavedBoardFont(),
  );

  /**
   * The player's chosen global text size. Persisted to localStorage and mirrored
   * onto <html>'s `data-text-size` so the token CSS can scale the root font-size
   * (which every `rem`-based text element follows). Change it via `setTextSize`
   * (keeps storage + DOM in sync).
   */
  public readonly textSize: IObservableProperty<TextSize> = new ObservableProperty<TextSize>(
    readSavedTextSize(),
  );

  constructor() {
    applyBoardFontToDocument(this.boardFont.value);
    applyTextSizeToDocument(this.textSize.value);
    if (typeof window !== "undefined") {
      window.addEventListener("popstate", this._onPopState);
      window.addEventListener("focus", this._onWindowFocus);
      window.addEventListener("pageshow", this._onPageShow);
      document.addEventListener("visibilitychange", this._onVisibilityChange);
      this.currentScreen.subscribe(this._onScreenChanged, { notifyWithCurrentValue: false });
    }
  }

  /**
   * Choose the play-surface font. Updates the observable (so the UI reacts),
   * persists the choice, and applies it to the document immediately.
   */
  public setBoardFont(font: BoardFont): void {
    this.boardFont.value = font;
    applyBoardFontToDocument(font);
    try {
      localStorage.setItem(BOARD_FONT_KEY, font);
    } catch {
      // Ignore storage failures (e.g. private mode); the in-memory value still applies.
    }
  }

  /**
   * Choose the global text size. Updates the observable (so the UI reacts),
   * persists the choice, and applies it to the document immediately.
   */
  public setTextSize(size: TextSize): void {
    this.textSize.value = size;
    applyTextSizeToDocument(size);
    try {
      localStorage.setItem(TEXT_SIZE_KEY, size);
    } catch {
      // Ignore storage failures (e.g. private mode); the in-memory value still applies.
    }
  }

  /**
   * The browser is the single source of truth for navigation. Every backable
   * layer — each screen you push, and each full-screen overlay you open — is one
   * real history entry described by the `NavState` we stash in `history.state`.
   * We never fight a user Back: whenever the browser moves (Back button, iOS /
   * Android swipe, or our own history.back()/go()), we simply render whatever
   * the resulting entry says. That keeps what WebKit paints *during* an
   * interactive swipe-back matching what we render once the gesture completes.
   */
  private _onPopState = (): void => {
    this._applyNavState(this._currentNavState());
  };

  private _currentNavState(): NavState {
    const state = typeof window !== "undefined" ? (window.history.state as NavState | null) : null;
    if (state && typeof state.depth === "number") {
      return state;
    }
    // No state yet (the initial page-load entry): treat it as the depth-0 root.
    return { screen: this.currentScreen.value, depth: 0, overlay: null };
  }

  private _applyNavState(state: NavState): void {
    if (
      state.sessionGeneration !== undefined &&
      state.sessionGeneration !== this._sessionGeneration
    ) {
      this.currentPuzzle.value = undefined;
      this.currentOverlay.value = undefined;
      this.currentScreen.value = "Loading";
      return;
    }
    if (PUZZLE_SCREENS.has(state.screen)) {
      // Re-entering a puzzle screen (e.g. a Forward swipe after backing out to
      // Stats, which cleared currentPuzzle): restore the puzzle the entry
      // describes so it renders instead of going blank. Done before switching
      // the screen so the render sees a matching puzzle.
      if (state.puzzleDate) {
        const puzzle = this.findPuzzleByDate(state.puzzleDate);
        if (puzzle) {
          this.currentPuzzle.value = puzzle;
        }
      }
    } else if (!state.overlay) {
      // Returning to a plain (non-overlay) non-puzzle screen means we've left any
      // puzzle behind, so drop the reference and the screen renders fresh.
      this.currentPuzzle.value = undefined;
    }
    this.currentScreen.value = state.screen;
    this.currentOverlay.value = state.overlay ?? undefined;
    if (state.overlay !== "feedback") {
      this.feedbackPrefill.value = undefined;
    }
  }

  private _pushNavState(state: NavState): void {
    if (typeof window !== "undefined") {
      if (state.screen === "Operations") {
        window.history.pushState(state, "", "/operations");
      } else {
        window.history.pushState(state, "");
      }
    }
  }

  private _replaceNavState(state: NavState): void {
    if (typeof window !== "undefined") {
      window.history.replaceState(state, "");
    }
  }

  /**
   * The date to stamp into a NavState for `screen`, so re-entering that entry
   * can restore `currentPuzzle`. Only puzzle screens carry a date.
   */
  private _puzzleDateFor(screen: Screen): SimpleDate | undefined {
    return PUZZLE_SCREENS.has(screen) ? this.currentPuzzle.value?.date : undefined;
  }

  /** Push a screen on top of the current one (adds a history entry). */
  public navigateTo(screen: Screen): void {
    const depth = this._currentNavState().depth + 1;
    this._pushNavState({
      screen,
      depth,
      overlay: null,
      puzzleDate: this._puzzleDateFor(screen),
      sessionGeneration: this._sessionGeneration,
    });
    this.currentScreen.value = screen;
    this.currentOverlay.value = undefined;
  }

  /**
   * Open the Leaderboard fresh, starting on the most recent day. Resetting the
   * selected day here (rather than persisting the last-viewed day) means opening
   * the leaderboard from the Home screen always lands on today, while navigating
   * into a friend's play and back keeps the day you were on (that path doesn't
   * pass through here — it's a history Back that remounts the screen).
   */
  public openLeaderboard(): void {
    this.leaderboardDate.value = undefined;
    this.navigateTo("Leaderboard");
  }

  /**
   * Go back one entry. We ask the browser to go back and let the resulting
   * popstate re-render from history — the same path a hardware Back or swipe
   * takes, so there is only ever one code path and no double-navigation.
   */
  public navigateBack(): void {
    if (typeof window !== "undefined") {
      window.history.back();
    }
  }

  /** Open a full-screen overlay as its own history entry. */
  public openOverlay(overlay: OverlayId): void {
    if (this.currentOverlay.value === overlay) {
      return;
    }
    const depth = this._currentNavState().depth + 1;
    this._pushNavState({
      screen: this.currentScreen.value,
      depth,
      overlay,
      puzzleDate: this._puzzleDateFor(this.currentScreen.value),
      sessionGeneration: this._sessionGeneration,
    });
    this.currentOverlay.value = overlay;
  }

  public openFeedback(message?: string): void {
    this.feedbackPrefill.value = message ? { sentiment: "negative", message } : undefined;
    this.openOverlay("feedback");
  }

  /** Close the open overlay by going back to the entry beneath it. */
  public closeOverlay(): void {
    if (typeof window !== "undefined") {
      window.history.back();
    }
  }

  /** Unwind to the app root (depth 0) in a single history move. */
  public navigateToRoot(): void {
    const steps = this._currentNavState().depth;
    if (steps > 0 && typeof window !== "undefined") {
      window.history.go(-steps);
    }
  }

  /**
   * Make the current history entry a clean depth-0 baseline showing `base`,
   * with no forward entries to return to. Used at boot and when entering /
   * loading a test scenario. Leaves `currentPuzzle` untouched so callers can
   * seed it immediately before/after (see loadTestScenario).
   */
  public resetScreenStack(base: Screen, url?: string): void {
    const state: NavState = {
      screen: base,
      depth: 0,
      overlay: null,
      sessionGeneration: this._sessionGeneration,
    };
    if (typeof window !== "undefined" && url) {
      window.history.replaceState(state, "", url);
    } else {
      this._replaceNavState(state);
    }
    this.currentScreen.value = base;
    this.currentOverlay.value = undefined;
  }

  /**
   * Whether today's puzzle is ready, being fetched, or not-yet-published. Drives
   * the Home screen's hero: a loading card while we poke the server + retry, and
   * a "not ready yet" card if it still hasn't appeared. Set during load().
   */
  public readonly todayPuzzleStatus: IObservableProperty<TodayPuzzleStatus> =
    new ObservableProperty<TodayPuzzleStatus>("idle");

  /* ---------------------- Friend leaderboard state ---------------------- */

  /** The signed-in player's roster (self + friends + outstanding invites), or
   *  null until loaded / when the player has no social record yet. */
  public readonly roster: IObservableProperty<Roster | null> =
    new ObservableProperty<Roster | null>(null);

  /** True while the roster is being (re)fetched. */
  public readonly rosterLoading: IObservableProperty<boolean> = new ObservableProperty<boolean>(
    false,
  );

  /** The current friend leaderboard view (per selected date). */
  public readonly leaderboard: IObservableProperty<LeaderboardView | null> =
    new ObservableProperty<LeaderboardView | null>(null);

  /** Server-verified self entitlement plus public CDN friend recognition.
   * Unknown/error states always deny premium extras. */
  public readonly premium: IObservableProperty<PremiumState> = new ObservableProperty<PremiumState>(
    { ...EMPTY_PREMIUM_STATE },
  );

  /** Friendly result from the Patreon OAuth callback, shown once on Home. */
  public readonly premiumNotice: IObservableProperty<PremiumNotice | null> =
    new ObservableProperty<PremiumNotice | null>(null);

  public readonly storeRecoveryError: IObservableProperty<string | null> =
    new ObservableProperty<string | null>(null);

  /** One-time paid membership or tier-upgrade thank-you for the current player. */
  public readonly premiumCelebration: IObservableProperty<PremiumCelebration | null> =
    new ObservableProperty<PremiumCelebration | null>(null);

  /** Accepted app-return entitlement checks, exposed only by the visible test UI. */
  public readonly testPremiumResumeRequestCount: IObservableProperty<number> =
    new ObservableProperty<number>(0);

  /** Selected scenario, retained when returning to the visible test picker. */
  public readonly currentTestScenarioId: IObservableProperty<string | null> =
    new ObservableProperty<string | null>(null);

  /** Recent account-scoped intent to purchase a Patreon membership. */
  public readonly patreonPurchaseIntent: IObservableProperty<boolean> =
    new ObservableProperty<boolean>(false);

  public readonly patreonPurchaseState: IObservableProperty<PatreonPurchaseState> =
    new ObservableProperty<PatreonPurchaseState>("idle");

  /**
   * The leaderboard day the user has selected. Held here (rather than in the
   * LeaderboardScreen's local state) so it survives that screen unmounting when
   * you open a friend's play and then press Back — the date you were viewing is
   * preserved instead of snapping back to today. Undefined means "the most
   * recent day" (today); `openLeaderboard` resets to it on a fresh entry.
   */
  public readonly leaderboardDate: IObservableProperty<SimpleDate | undefined> =
    new ObservableProperty<SimpleDate | undefined>(undefined);

  /** The play (board + found words) currently being viewed from the leaderboard,
   *  or null when the FriendPlay screen isn't active. */
  public readonly friendPlay: IObservableProperty<FriendPlayView | null> =
    new ObservableProperty<FriendPlayView | null>(null);

  /** A pending invite captured from an /i/{code} deep link, awaiting the user's
   *  accept/decline on the Friends screen. Null when there's nothing pending. */
  public readonly pendingInvite: IObservableProperty<{
    code: string;
    preview: InvitePreview | null;
  } | null> = new ObservableProperty<{ code: string; preview: InvitePreview | null } | null>(null);

  /**
   * Clear all in-memory session state and return to the Loading screen.
   * Called on sign-out so when a different account signs in next, the
   * LoadingScreen's useEffect re-runs load() with the new playerId
   * instead of showing the previous user's puzzles.
   */
  public resetSession(): void {
    if (this._storeExpiryTimer !== undefined) window.clearTimeout(this._storeExpiryTimer);
    this._storeExpiryTimer = undefined;
    const previousPlayerId =
      this._session?.userGuid ??
      (authState.value.status === "signedIn" ? authState.value.identity.playerId : undefined);
    this._cancelTodayPolling();
    this._cancelPendingScorePolling();
    const premiumGeneration = this._premiumDataGeneration.invalidate();
    this._friendPlayLoadGeneration++;
    this._sessionGeneration++;
    this._session = undefined;
    this._confirmedPaidMembership = undefined;
    this.currentPuzzle.value = undefined;
    this.todayPuzzleStatus.value = "idle";
    this.roster.value = null;
    this.rosterLoading.value = false;
    this.leaderboard.value = null;
    this.leaderboardDate.value = undefined;
    this.friendPlay.value = null;
    this.premium.value = {
      ...EMPTY_PREMIUM_STATE,
      generation: premiumGeneration,
    };
    this.premiumNotice.value = null;
    this.storeRecoveryError.value = null;
    this.premiumCelebration.value = null;
    this.pendingInvite.value = null;
    this.operationsDashboard.value = { status: "idle" };
    this._pendingPatreonResult = null;
    this._pendingPatreonProof = null;
    this._pendingPatreonPlayerId = null;
    this._testPremiumRefreshSequence = undefined;
    this._testPremiumRefreshIndex = 0;
    this._testPremiumRefreshDelayMs = 0;
    this._testPurchaseRetryDelaysMs = undefined;
    this._testPlayerStats = undefined;
    this._premiumRefreshRequest = null;
    this._lastPremiumResumeRefreshAt = 0;
    this._resetPatreonPurchaseRetries();
    this.testPremiumResumeRequestCount.value = 0;
    this.currentTestScenarioId.value = null;
    this.patreonPurchaseIntent.value = false;
    this.patreonPurchaseState.value = "idle";
    try {
      if (previousPlayerId) {
        sessionStorage.removeItem(playerSessionKey(PENDING_PATREON_RESULT_KEY, previousPlayerId));
        sessionStorage.removeItem(playerSessionKey(PENDING_PATREON_PROOF_KEY, previousPlayerId));
        sessionStorage.removeItem(
          playerSessionKey(PENDING_PATREON_MEMBERSHIP_KEY, previousPlayerId),
        );
      }
    } catch {
      // In-memory state is already cleared.
    }
    this.resetScreenStack("Loading");
  }

  /**
   * Test-mode entry point (see src/testMode.ts). Lands on the scenario picker
   * so a canned game state can be chosen, without auth or any backend call.
   */
  public enterTestMode(): void {
    this._premiumDataGeneration.invalidate();
    this._sessionGeneration++;
    this._session = undefined;
    this._confirmedPaidMembership = undefined;
    this.currentPuzzle.value = undefined;
    this.premium.value = { ...EMPTY_PREMIUM_STATE };
    this.premiumNotice.value = null;
    this.storeRecoveryError.value = null;
    this.premiumCelebration.value = null;
    this._testPremiumRefreshSequence = undefined;
    this._testPremiumRefreshIndex = 0;
    this._testPremiumRefreshDelayMs = 0;
    this._testPurchaseRetryDelaysMs = undefined;
    this._testPlayerStats = undefined;
    this._premiumRefreshRequest = null;
    this._lastPremiumResumeRefreshAt = 0;
    this._resetPatreonPurchaseRetries();
    this.testPremiumResumeRequestCount.value = 0;
    this.patreonPurchaseIntent.value = false;
    this.patreonPurchaseState.value = "idle";
    setTestSignedIn();
    this.resetScreenStack("TestScenarios");
  }

  /**
   * Load a named test scenario (see getTestScenarios): build its canned session
   * and jump to the scenario's target screen. No-op if the id is unknown.
   */
  public loadTestScenario(scenarioId: string): void {
    const scenario = getTestScenarios().find((s) => s.id === scenarioId);
    if (!scenario) {
      console.warn(`Unknown test scenario: ${scenarioId}`);
      return;
    }
    const setup = scenario.build();
    this.currentTestScenarioId.value = scenario.id;
    this._premiumRefreshRequest = null;
    this._lastPremiumResumeRefreshAt = 0;
    this.testPremiumResumeRequestCount.value = 0;
    this._sessionGeneration++;
    const testPlayerId = setup.playerId ?? TEST_SELF_ID;
    const testPlayerName = setup.playerName ?? "Test Player";
    try {
      localStorage.removeItem(playerSessionKey(PREMIUM_ENTITLEMENT_CACHE_KEY, testPlayerId));
    } catch {
      // Test scenarios remain deterministic in memory when storage is unavailable.
    }
    setTestSignedIn(testPlayerId, testPlayerName);
    this._session = new SessionV1({
      version: "1",
      userGuid: testPlayerId,
      puzzles: setup.puzzles,
    });
    const puzzles = this._session.puzzles;
    this.currentPuzzle.value =
      setup.screen === "Puzzle" || setup.screen === "ReviewPuzzle"
        ? puzzles[setup.currentPuzzleIndex]
        : undefined;
    this.todayPuzzleStatus.value = setup.todayPuzzleStatus ?? "idle";
    // Seed the offline friend roster so the Friends/Leaderboard screens have
    // data to show without any backend call (preserving a name typed earlier).
    const previousSelf = this.roster.value?.self;
    this.roster.value = buildTestRoster(
      setup.playerName ??
        (previousSelf?.playerId === testPlayerId ? previousSelf.displayName : undefined),
      testPlayerId,
    );
    const testTier = setup.premiumTier === undefined ? "gold" : setup.premiumTier;
    const premiumVerificationError = setup.premiumVerificationError === true;
    const premiumStatus = premiumVerificationError ? "error" : setup.premiumStatus ?? "ready";
    const verifiedTestTier = premiumStatus === "ready" ? testTier : null;
    const testPatreonLinked =
      setup.premiumLinked ?? (premiumStatus === "ready" && testTier !== null);
    const testPatreonTier = setup.premiumSource === "complimentary" ? null : verifiedTestTier;
    const participants = [this.roster.value.self, ...this.roster.value.friends];
    const premiumGeneration = this._premiumDataGeneration.start();
    this._testPremiumRefreshSequence =
      setup.premiumRefreshSequence ??
      (setup.premiumRefreshOutcome !== undefined ? [setup.premiumRefreshOutcome] : undefined);
    this._testPlayerStats = setup.playerStats;
    this._testPremiumRefreshIndex = 0;
    this._testPremiumRefreshDelayMs = setup.premiumRefreshDelayMs ?? 0;
    this._testPurchaseRetryDelaysMs = setup.premiumPurchaseRetryDelaysMs;
    this._confirmedPaidMembership = premiumStatus === "ready" ? {
      playerId: testPlayerId,
      environment: "Production",
      tier: setup.premiumPreviousPaidTier === undefined ? testPatreonTier : setup.premiumPreviousPaidTier,
    } : undefined;
    if (setup.premiumPurchaseIntent === "restore") {
      this._restorePatreonPurchaseIntent(testPlayerId);
    } else {
      this._setPatreonPurchaseIntentForTest(testPlayerId, setup.premiumPurchaseIntent === true);
    }
    this.premiumCelebration.value = setup.premiumCelebration ?? null;
    this.premiumNotice.value = setup.premiumNotice ?? null;
    this.storeRecoveryError.value = null;
    this.premium.value = {
      generation: premiumGeneration,
      status: premiumStatus,
      tier: verifiedTestTier,
      source: verifiedTestTier ? setup.premiumSource ?? "patreon" : null,
      patreon: {
        configured: true,
        linked: testPatreonLinked,
        tier: testPatreonTier,
        displayName: testPatreonTier ? "Test Patron" : undefined,
        membershipStatus: testPatreonTier ? "active_patron" : "none",
      },
      players:
        premiumStatus !== "ready"
          ? {}
          : Object.fromEntries(
              participants.map((participant, index) => [
                participant.playerId,
                participant.playerId === this.roster.value!.self.playerId
                  ? verifiedTestTier
                  : (["bronze", "silver", "gold", null] as const)[(index - 1) % 4],
              ]),
            ),
      error: premiumVerificationError ? "Backer access could not be verified." : undefined,
    };
    // Seed a pending deep-link invite when the scenario asks for one, so the
    // Friends screen's accept/dismiss banner (the /?invite= landing) can be
    // exercised offline. Clear it otherwise so scenarios don't leak state.
    this.pendingInvite.value = setup.pendingInvite ?? null;
    this.operationsDashboard.value = setup.operationsDashboard
      ? { status: "loaded", data: setup.operationsDashboard }
      : { status: "idle" };
    // Push the scenario's target screen on top of the test picker so backing
    // out of it returns here rather than dead-ending.
    this.resetScreenStack("TestScenarios");
    this.navigateTo(setup.screen);
  }

  public openOperationsDashboard(): void {
    this.navigateTo("Operations");
    void this.loadOperationsDashboard();
  }

  public async loadOperationsDashboard(): Promise<void> {
    if (isTestMode()) {
      if (this.operationsDashboard.value.status !== "loaded") {
        this.operationsDashboard.value = {
          status: "loaded",
          data: buildTestOperationsDashboard(),
        };
      }
      return;
    }
    const token = getSessionToken();
    if (!token) {
      this.operationsDashboard.value = { status: "unauthorized" };
      return;
    }
    this.operationsDashboard.value = { status: "loading" };
    const response = await fetchOperationsDashboard(token);
    this.operationsDashboard.value =
      response.result === "Success"
        ? { status: "loaded", data: response.data }
        : { status: response.result === "Unauthorized" ? "unauthorized" : "error" };
  }

  public async load(options?: LoadOptions): Promise<LoadResult> {
    const auth = authState.value;
    if (auth.status !== "signedIn") {
      return "NotSignedIn";
    }
    const playerId = auth.identity.playerId;
    if (
      options === "OverrideSavedAccountWithNewOne" ||
      (this._session !== undefined && this._session.userGuid !== playerId)
    ) {
      this.resetSession();
    }
    const generation = this._sessionGeneration;
    this._restorePatreonPurchaseIntent(playerId);

    const savedSessionData = this._loadLocalSessionData(playerId);
    const sessionData: ISessionDataV1 = savedSessionData ?? this._createNewSessionData(playerId);

    const refreshResult = await this._refreshPuzzlesFromBackend(sessionData);
    if (!this._isCurrentPlayer(playerId, generation)) {
      return "NotSignedIn";
    }
    if (refreshResult === "Error") {
      return "Error";
    }

    sessionData.puzzles.sort((a: IPuzzleRemoteDataV1, b: IPuzzleRemoteDataV1) =>
      compareSimpleDates(a.date, b.date),
    );
    this._session = new SessionV1(sessionData);
    this._saveLocalSessionData(this._session);
    const finalizedPatreonLink = await this._finalizePendingPatreonLink(playerId, generation);
    if (!this._isCurrentPlayer(playerId, generation)) {
      return "NotSignedIn";
    }

    // If today's puzzle wasn't published yet, poke the server to run the daily
    // rollover and retry in the background. We return "Success" immediately so
    // the app lands on Home with a loading card, rather than blocking startup on
    // the ~30s retry budget. Real user demand thus becomes a reliable trigger.
    const today = getSimpleDateInSeattle();
    if (this.findPuzzleByDate(today) === undefined) {
      this.todayPuzzleStatus.value = "loading";
      void this._pokeForTodaysPuzzleInBackground(playerId);
    } else {
      this.todayPuzzleStatus.value = "idle";
    }

    // Load the friend roster + any pending deep-link invite in the background so
    // Home paints immediately; the Friends screen reacts to the observables.
    void this.loadRoster();
    void this._restorePendingInvite();
    void this.loadPremiumStatus(!finalizedPatreonLink).then(() => this._recoverStorePurchases());

    // If any finished past puzzle is still missing its rare-word bonus (scored
    // by the daily rollover), poll for it in the background so the review screen
    // fills in on its own instead of the user having to reload.
    this._startPendingScorePollingIfNeeded();

    return "Success";
  }

  public save(): void {
    if (this._session === undefined) {
      console.error("Tried to save, but had no session to save");
      return;
    }
    this._saveLocalSessionData(this._session);
  }

  public async submitPuzzle(): Promise<SubmitSolutionResult> {
    if (isTestMode()) {
      // Test mode: never contact the backend. Treat submission as a benign
      // no-op so the play screen can be exercised end-to-end without a server.
      console.log("Test mode: skipping real puzzle submission (no-op).");
      return "Success";
    }
    if (localStorage.getItem(OFFLINE_MODE_KEY) === "1") {
      console.error("Asked to submit a puzzle, but in offline mode");
      return "Error";
    }
    if (this.currentPuzzle.value === undefined) {
      console.error("Asked to submit a puzzle, but there is no current puzzle");
      return "Error";
    }
    if (this._session === undefined) {
      console.error("Tried to submit a solution, but had no active session");
      return "Error";
    }

    this.save();

    const puzzle = this.currentPuzzle.value;
    const puzzleId = toPuzzleId(puzzle.date);
    const words = puzzle.foundWords.value.map((w: IWordInPuzzleV1) => ({
      word: w.word.toUpperCase(),
      path: puzzle.wordPaths.get(w.word.toUpperCase()) ?? [],
    }));

    // Submission requires a bearer token the backend can verify. Prefer the
    // session JWT minted by /api/me on sign-in: it lives in localStorage and
    // survives webview/app restarts, so no Apple/Google prompt is required.
    // Only fall back to a fresh provider ID token if no session JWT is cached
    // (e.g. older clients before the session-token rollout, or right after a
    // signOut). If the backend rejects the session token as expired, we clear
    // it and retry with a fresh provider token.
    const auth = authState.value;
    if (auth.status !== "signedIn") {
      return "NotSignedIn";
    }

    const playerId = auth.identity.playerId;
    const sessionGeneration = this._sessionGeneration;
    const response = await this._callWithBearer((bearer) => submitPlay(bearer, puzzleId, words));
    if (!this._isCurrentPlayer(playerId, sessionGeneration)) {
      return "NotSignedIn";
    }

    switch (response.result) {
      case "Success":
        puzzle.played = true;
        puzzle.regularPoints.value = response.regularPoints;
        this.save();
        return "Success";
      case "AlreadySubmitted":
        return "SolutionAlreadySubmitted";
      case "NotTodaysPuzzle":
        return "InvalidDate";
      case "Unauthorized":
        return "NotSignedIn";
      case "PuzzleNotFound":
      case "Error":
      default:
        return "Error";
    }
  }

  public async getPlayerStats(): Promise<IPlayerStats | undefined> {
    let playerStats: IPlayerStats | undefined;
    if (isTestMode()) {
      // No test-player.json exists on the CDN, so serve canned stats to exercise
      // the Stats screen (see buildTestPlayerStats in testMode.ts).
      playerStats = this._testPlayerStats ?? buildTestPlayerStats();
    } else if (this._session === undefined) {
      console.error("Tried to get player stats, but had no active session");
      return undefined;
    } else {
      playerStats = await fetchPlayerStats(this._session.userGuid);
    }
    const todaysPuzzle = this.findPuzzleByDate(getSimpleDateInSeattle());
    return todaysPuzzle ? mergePendingPuzzleStats(playerStats, todaysPuzzle) : playerStats;
  }

  public async getFeedbackDiagnostics(): Promise<FeedbackDiagnostics> {
    const screen = this.currentScreen.value;
    const puzzle = this.currentPuzzle.value;
    return {
      ...(await collectFeedbackDiagnostics(screen)),
      ...(puzzle ? { puzzleDate: toPuzzleId(puzzle.date) } : {}),
    };
  }

  /** Submit feedback with only the diagnostic snapshot reviewed by the reporter. */
  public async submitFeedback(
    sentiment: "positive" | "negative" | "feature-request",
    message: string,
    diagnostics?: FeedbackDiagnostics,
  ): Promise<FeedbackSubmitResult> {
    if (isTestMode()) {
      console.log("Test mode: skipping real feedback submission (no-op).");
      return "Success";
    }

    const auth = authState.value;
    if (auth.status !== "signedIn") {
      return "NotSignedIn";
    }

    const context: FeedbackContext = {
      email: auth.identity.email,
      name: auth.identity.name,
      ...(sentiment === "negative" && diagnostics
        ? {
            appVersion: diagnostics.app.webVersion,
            screen: diagnostics.screen,
            route: diagnostics.route,
            puzzleDate: diagnostics.puzzleDate,
            platform: diagnostics.app.surface,
            userAgent: diagnostics.browser.userAgent,
            diagnostics: JSON.stringify(diagnostics, null, 2),
          }
        : {}),
    };

    const playerId = auth.identity.playerId;
    const sessionGeneration = this._sessionGeneration;
    const response = await this._callWithBearer((bearer) =>
      submitFeedback(bearer, { sentiment, message, context }),
    );
    if (!this._isCurrentPlayer(playerId, sessionGeneration)) {
      return "NotSignedIn";
    }

    switch (response.result) {
      case "Success":
        return "Success";
      case "Unauthorized":
        return "NotSignedIn";
      case "RateLimited":
        return "RateLimited";
      default:
        return "Error";
    }
  }

  public findPuzzleByDate(date: SimpleDate): IPuzzleV1 | undefined {
    return this._session?.puzzles.find((puzzle: IPuzzleV1) =>
      areSimpleDatesEqual(puzzle.date, date),
    );
  }

  /* ======================= Friend leaderboard ======================= */

  private _publishNamesOnlyFromExisting(generation: number, verificationFailed: boolean): void {
    const current = this.leaderboard.value;
    if (!current) return;
    this.leaderboard.value = {
      generation,
      puzzleId: current.puzzleId,
      loading: false,
      scoresVisible: false,
      verificationFailed: verificationFailed || undefined,
      rows: current.rows.map((row) => ({
        playerId: row.playerId,
        displayName: row.displayName,
        isSelf: row.isSelf,
        tier: null,
        entry: {
          playerId: row.playerId,
          played: false,
          scored: false,
          wordCount: 0,
          regularPoints: 0,
        },
      })),
    };
  }

  private _publishPremiumStatus(
    playerId: string,
    generation: number,
    status: PremiumStatus,
    confirmed = true,
  ): void {
    if (this._storeExpiryTimer !== undefined) window.clearTimeout(this._storeExpiryTimer);
    this.premium.value = {
      generation,
      status: "ready",
      tier: status.tier,
      source: status.source,
      subscription: status.subscription,
      billingEnvironment: status.billingEnvironment,
      patreon: status.patreon,
      players: { [playerId]: status.tier },
    };
    if (confirmed) {
      this._recognizePaidPremiumTier(
        playerId, status.subscription?.tier ?? status.patreon.tier, status.billingEnvironment,
      );
    }
    if (status.subscription) {
      const sessionGeneration = this._sessionGeneration;
      const delay = Math.max(0, Date.parse(status.subscription.expiresAt) - Date.now());
      this._storeExpiryTimer = window.setTimeout(
        () => {
          if (!this._isCurrentPlayer(playerId, sessionGeneration)) return;
          if (
            status.source !== "complimentary" &&
            status.subscription &&
            Date.parse(status.subscription.expiresAt) <= Date.now()
          ) {
            const expiryGeneration = this._premiumDataGeneration.invalidate();
            this._publishNamesOnlyFromExisting(expiryGeneration, false);
            this.premium.value = { ...EMPTY_PREMIUM_STATE, generation: expiryGeneration };
          }
          this._invalidateCachedPremiumStatus(playerId);
          void this.loadPremiumStatus(false);
        },
        Math.min(delay, 2_000_000_000),
      );
    }
    const currentLeaderboard = this.leaderboard.value;
    if (
      currentLeaderboard?.generation === generation &&
      !currentLeaderboard.scoresVisible &&
      !currentLeaderboard.loading
    ) {
      this.leaderboard.value = {
        ...currentLeaderboard,
        rows: currentLeaderboard.rows.map((row) => ({
          ...row,
          tier: this.premium.value.players[row.playerId] ?? null,
        })),
      };
    }
  }

  private _readCachedPremiumStatus(
    playerId: string,
  ): { status: PremiumStatus; automaticCheckDue: boolean } | null {
    const key = playerSessionKey(PREMIUM_ENTITLEMENT_CACHE_KEY, playerId);
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const cached = JSON.parse(raw) as {
        nextAutomaticEntitlementCheckAt?: unknown;
        status?: unknown;
      };
      if (!isPremiumStatus(cached.status)) {
        localStorage.removeItem(key);
        return null;
      }
      const nextCheckAt = cached.nextAutomaticEntitlementCheckAt;
      const now = Date.now();
      const automaticCheckDue =
        typeof nextCheckAt !== "number" ||
        !Number.isFinite(nextCheckAt) ||
        nextCheckAt <= now ||
        nextCheckAt > now + AUTOMATIC_ENTITLEMENT_CHECK_INTERVAL_MS ||
        (!!cached.status.subscription && Date.parse(cached.status.subscription.expiresAt) <= now);
      if (cached.status.subscription && Date.parse(cached.status.subscription.expiresAt) <= now) {
        return null;
      }
      return { status: cached.status, automaticCheckDue };
    } catch {
      return null;
    }
  }

  private _cachePremiumStatus(playerId: string, status: PremiumStatus): void {
    if (status.billingEnvironment === "Sandbox") {
      this._invalidateCachedPremiumStatus(playerId);
      return;
    }
    if (this.patreonPurchaseIntent.value && !status.patreon.tier) {
      this._invalidateCachedPremiumStatus(playerId);
      return;
    }
    try {
      localStorage.setItem(
        playerSessionKey(PREMIUM_ENTITLEMENT_CACHE_KEY, playerId),
        JSON.stringify({
          nextAutomaticEntitlementCheckAt: Date.now() + AUTOMATIC_ENTITLEMENT_CHECK_INTERVAL_MS,
          status,
        }),
      );
    } catch {
      // The successful status remains available for this app lifetime.
    }
  }

  private _invalidateCachedPremiumStatus(playerId: string): void {
    try {
      localStorage.removeItem(playerSessionKey(PREMIUM_ENTITLEMENT_CACHE_KEY, playerId));
    } catch {
      // The next explicit refresh still bypasses the cached status in this app lifetime.
    }
  }

  private _cacheCurrentPremiumStatus(playerId: string): void {
    const premium = this.premium.value;
    if (premium.status !== "ready" || premium.error) return;
    this._cachePremiumStatus(playerId, {
      tier: premium.tier,
      source: premium.source,
      subscription: premium.subscription,
      billingEnvironment: premium.billingEnvironment,
      patreon: premium.patreon,
    });
  }

  private _failPremiumVerification(
    generation: number,
    error: string,
    previous: PremiumState,
    preservePrevious: boolean,
    publishLeaderboard?: (failureGeneration: number) => void,
  ): boolean {
    if (
      preservePrevious &&
      previous.status === "ready" &&
      previous.tier &&
      (previous.source === "complimentary" ||
        !previous.subscription ||
        Date.parse(previous.subscription.expiresAt) > Date.now())
    ) {
      this.premium.value = {
        ...previous,
        generation,
        error,
      };
      return true;
    }
    const failureGeneration = this._premiumDataGeneration.invalidateIfCurrent(generation);
    if (failureGeneration === undefined) return false;
    if (publishLeaderboard) {
      publishLeaderboard(failureGeneration);
    } else {
      this._publishNamesOnlyFromExisting(failureGeneration, true);
    }
    this.premium.value = {
      generation: failureGeneration,
      status: "error",
      tier: null,
      source: null,
      patreon: this.premium.value.patreon,
      players: {},
      error,
    };
    return false;
  }

  private async _loadPremiumStatusForGeneration(
    playerId: string,
    sessionGeneration: number,
    generation: number,
    publishFailure?: (failureGeneration: number) => void,
    automatic = false,
  ): Promise<boolean> {
    if (automatic && nativeStore() !== "apple") {
      const cached = this._readCachedPremiumStatus(playerId);
      if (cached) {
        this._publishPremiumStatus(playerId, generation, cached.status, false);
        if (!cached.automaticCheckDue) {
          return cached.status.tier !== null;
        }
      }
    }
    const previous = this.premium.value;
    this.premium.value = {
      generation,
      status: previous.status === "idle" ? "loading" : previous.status,
      tier: previous.tier,
      source: previous.source,
      subscription: previous.subscription,
      billingEnvironment: previous.billingEnvironment,
      patreon: previous.patreon,
      players: previous.players,
      error: previous.error,
    };
    const response = await this._callWithBearer((bearer) => fetchPremiumStatus(bearer));
    if (
      !this._premiumDataGeneration.isCurrent(generation) ||
      !this._isCurrentPlayer(playerId, sessionGeneration)
    ) {
      return false;
    }
    if (response.result === "Success" && isPremiumStatus(response.status)) {
      const status = response.status;
      this._cachePremiumStatus(playerId, status);
      this._publishPremiumStatus(playerId, generation, status);
      return status.tier !== null;
    }
    return this._failPremiumVerification(
      generation,
      response.result === "NotSignedIn" || response.result === "Unauthorized"
        ? "Please sign in again to check backer access."
        : response.result === "Error"
          ? response.message ?? "Premium access could not be verified."
          : "The subscription service returned an invalid response.",
      previous,
      response.result === "Error",
      publishFailure,
    );
  }

  /** Refresh premium access from the authenticated backend. Test mode uses the
   * scenario's deterministic state and never contacts the server. */
  public async loadPremiumStatus(automatic = true): Promise<boolean> {
    if (isTestMode()) {
      return this.premium.value.tier !== null;
    }
    const auth = authState.value;
    if (auth.status !== "signedIn") {
      const generation = this._premiumDataGeneration.invalidate();
      this._publishNamesOnlyFromExisting(generation, false);
      this.premium.value = { ...EMPTY_PREMIUM_STATE, generation };
      return false;
    }
    const playerId = auth.identity.playerId;
    const sessionGeneration = this._sessionGeneration;
    const generation = this._premiumDataGeneration.start();
    this._publishNamesOnlyFromExisting(generation, false);
    return this._loadPremiumStatusForGeneration(
      playerId,
      sessionGeneration,
      generation,
      undefined,
      automatic,
    );
  }

  /**
   * Refresh premium state from the screen that owns the action. Leaderboard
   * refreshes use its full coherent load so entitlement, public score reads,
   * and public friend projections share one generation.
   */
  public async refreshPremiumAccess(automatic = false): Promise<boolean> {
    if (isTestMode() && this._testPremiumRefreshSequence?.length) {
      const previous = this.premium.value;
      const outcome =
        this._testPremiumRefreshSequence[
          Math.min(this._testPremiumRefreshIndex, this._testPremiumRefreshSequence.length - 1)
        ];
      this._testPremiumRefreshIndex++;
      const generation = this._premiumDataGeneration.start();
      if (this._testPremiumRefreshDelayMs > 0) {
        this.premium.value = {
          ...this.premium.value,
          generation,
          status: previous.status === "idle" ? "loading" : previous.status,
          tier: previous.tier,
          source: previous.source,
          players: previous.players,
          error: previous.error,
        };
        await new Promise((resolve) => setTimeout(resolve, this._testPremiumRefreshDelayMs));
        if (!this._premiumDataGeneration.isCurrent(generation)) return false;
      }
      const participants = this.roster.value
        ? [this.roster.value.self, ...this.roster.value.friends]
        : [];
      const patreonTier = outcome === "error" ? previous.patreon.tier : outcome;
      const retainComplimentaryAccess =
        outcome !== "error" &&
        previous.source === "complimentary" &&
        premiumTierRank(previous.tier) >= premiumTierRank(patreonTier);
      const effectiveTier = retainComplimentaryAccess ? previous.tier : patreonTier;
      const effectiveSource = retainComplimentaryAccess
        ? previous.source
        : patreonTier
          ? "patreon"
          : null;
      this.premium.value =
        outcome === "error" && previous.tier
          ? {
              ...previous,
              generation,
              error: "Backer access could not be verified.",
            }
          : {
              ...this.premium.value,
              generation,
              status: outcome === "error" ? "error" : "ready",
              tier: effectiveTier,
              source: effectiveSource,
              patreon: {
                ...this.premium.value.patreon,
                linked: previous.patreon.linked,
                tier: patreonTier,
                displayName: patreonTier ? "Test Patron" : undefined,
                membershipStatus: patreonTier ? "active_patron" : "none",
              },
              players:
                outcome === "error"
                  ? {}
                  : Object.fromEntries(
                      participants.map((participant, index) => [
                        participant.playerId,
                        participant.playerId === this.roster.value?.self.playerId
                          ? effectiveTier
                          : (["bronze", "silver", "gold", null] as const)[(index - 1) % 4],
                      ]),
                    ),
              error: outcome === "error" ? "Backer access could not be verified." : undefined,
            };
      const auth = authState.value;
      if (outcome !== "error" && auth.status === "signedIn") {
        this._recognizePaidPremiumTier(auth.identity.playerId, patreonTier);
      }
    }

    if (this.currentScreen.value === "Leaderboard") {
      const selected = this.leaderboardDate.value;
      const activeDate =
        (selected &&
          this.puzzles.find((puzzle) => areSimpleDatesEqual(puzzle.date, selected))?.date) ??
        this.puzzles.slice().sort((a, b) => compareSimpleDates(b.date, a.date))[0]?.date;
      if (activeDate) {
        await this.loadLeaderboard(activeDate, automatic);
        return this.premium.value.status === "ready" && this.premium.value.tier !== null;
      }
    }

    return this.loadPremiumStatus(automatic);
  }

  private async _refreshPremiumAccessOnResume(): Promise<boolean> {
    const auth = authState.value;
    if (
      auth.status !== "signedIn" ||
      this.currentScreen.value === "Loading" ||
      this.currentScreen.value === "Connecting" ||
      this.currentScreen.value === "TestScenarios" ||
      this.premium.value.status === "loading"
    ) {
      return false;
    }
    this._restorePatreonPurchaseIntent(auth.identity.playerId);
    if (this._premiumRefreshRequest) {
      return this._premiumRefreshRequest;
    }
    const playerId = auth.identity.playerId;
    if (!this.patreonPurchaseIntent.value) {
      const cached = this._readCachedPremiumStatus(playerId);
      if (cached && !cached.automaticCheckDue && this.currentScreen.value !== "Leaderboard") {
        const generation = this._premiumDataGeneration.start();
        this._publishPremiumStatus(playerId, generation, cached.status, false);
        return cached.status.tier !== null;
      }
    }
    const now = Date.now();
    if (now - this._lastPremiumResumeRefreshAt < PREMIUM_RESUME_REFRESH_COOLDOWN_MS) {
      return this.premium.value.status === "ready" && this.premium.value.tier !== null;
    }
    this._lastPremiumResumeRefreshAt = now;
    const sessionGeneration = this._sessionGeneration;
    const screen = this.currentScreen.value;
    if (this.patreonPurchaseIntent.value) {
      this._resetPatreonPurchaseRetries();
      this.patreonPurchaseState.value = "checking";
    }
    let hasPremiumAccess = false;
    let outcome: PremiumRefreshOutcome = "error";
    try {
      hasPremiumAccess = await this._runPremiumAccessRefresh(true);
      outcome = this._premiumRefreshOutcome();
      if (outcome !== "error") {
        this._cacheCurrentPremiumStatus(playerId);
      }
    } finally {
      this._handlePatreonPurchaseRefresh(playerId, sessionGeneration, screen, outcome);
    }
    return hasPremiumAccess;
  }

  private _runPremiumAccessRefresh(automatic = false): Promise<boolean> {
    if (this._premiumRefreshRequest) {
      return this._premiumRefreshRequest;
    }
    if (isTestMode()) {
      this.testPremiumResumeRequestCount.value++;
    }
    const refresh = this.refreshPremiumAccess(automatic);
    this._premiumRefreshRequest = refresh;
    const clear = () => {
      if (this._premiumRefreshRequest === refresh) {
        this._premiumRefreshRequest = null;
      }
    };
    void refresh.then(clear, clear);
    return refresh;
  }

  /** User-requested retry bypasses the return cooldown but shares in-flight work. */
  public async forcePremiumAccessRefresh(): Promise<PremiumRefreshOutcome> {
    const auth = authState.value;
    const playerId = auth.status === "signedIn" ? auth.identity.playerId : null;
    const sessionGeneration = this._sessionGeneration;
    const screen = this.currentScreen.value;
    this._lastPremiumResumeRefreshAt = Date.now();
    if (this.patreonPurchaseIntent.value) {
      this._cancelPatreonPurchaseRetryTimer();
      this.patreonPurchaseState.value = "checking";
    }
    let outcome: PremiumRefreshOutcome = "error";
    try {
      await this._runPremiumAccessRefresh(false);
      outcome = this._premiumRefreshOutcome();
      if (playerId && outcome !== "error") {
        this._cacheCurrentPremiumStatus(playerId);
      }
    } finally {
      if (playerId) {
        this._handlePatreonPurchaseRefresh(playerId, sessionGeneration, screen, outcome);
      }
    }
    return outcome;
  }

  /** Visible test-mode control for exercising the real focus, visibility, and
   * pageshow burst without leaving the local test UI. */
  public async simulateTestAppReturn(): Promise<void> {
    if (!isTestMode()) return;
    window.dispatchEvent(new Event("focus"));
    document.dispatchEvent(new Event("visibilitychange"));
    window.dispatchEvent(new Event("pageshow"));
    if (this._premiumRefreshRequest) {
      await this._premiumRefreshRequest;
    }
  }

  /** Native shells call this from Capacitor's appStateChange resume event. */
  public async handleAppResume(): Promise<void> {
    const auth = authState.value;
    if (auth.status === "signedIn" && this._pendingPatreonResult) {
      await this._finalizePendingPatreonLink(auth.identity.playerId, this._sessionGeneration);
    }
    await this._refreshPremiumAccessOnResume();
    await this._recoverStorePurchases();
  }

  private async _recoverStorePurchases(): Promise<void> {
    const current = authState.value;
    const playerId = current.status === "signedIn" ? current.identity.playerId : null;
    const generation = this._sessionGeneration;
    if (
      nativeStore() &&
      window.Capacitor?.Plugins?.WirderBilling &&
      authState.value.status === "signedIn" &&
      !this._storeOperation &&
      !isTestMode()
    ) {
      try {
        await this.restoreStorePurchases(false);
      } catch (error) {
        console.error("Background store purchase recovery failed; retry in Settings");
        recordDiagnosticEvent("subscription-recovery", { outcome: "failed" });
        if (!playerId || !this._isCurrentPlayer(playerId, generation)) return;
        this.storeRecoveryError.value =
          error instanceof Error
            ? error.message
            : "Could not recover store purchases. Please try Restore Purchases.";
      }
    }
  }

  private _storeOperation: Promise<string> | null = null;
  private _storeExpiryTimer: number | undefined;

  public async manageStoreSubscriptions(): Promise<string> {
    const context = {
      store: nativeStore(),
      billingEnvironment: this.premium.value.billingEnvironment,
    };
    let stage = "opening";
    recordDiagnosticEvent("subscription-management", { ...context, stage });
    try {
      const plugin = billingPlugin();
      if (!plugin.manageSubscriptions)
        throw new Error("Please update the app to manage subscriptions.");
      await plugin.manageSubscriptions();
      recordDiagnosticEvent("subscription-management", {
        ...context,
        stage: "closed",
        message: "Subscription management closed.",
      });
      stage = "refreshing-access";
      const outcome = await this.forcePremiumAccessRefresh();
      recordDiagnosticEvent("subscription-management", {
        ...context,
        stage,
        outcome,
      });
      if (outcome === "error")
        throw new Error(
          "Could not refresh premium access after subscription management. Please try Restore Purchases.",
        );
      return "";
    } catch (error) {
      recordDiagnosticEvent("subscription-management", {
        ...context,
        stage,
        outcome: "failed",
        error,
      });
      throw error;
    }
  }

  public resetTestSubscription(): Promise<string> {
    if (this._storeOperation) return this._storeOperation;
    const operation = this._resetTestSubscription();
    this._storeOperation = operation;
    void operation.then(
      () => {
        this._storeOperation = null;
      },
      () => {
        this._storeOperation = null;
      },
    );
    return operation;
  }

  private async _resetTestSubscription(): Promise<string> {
    if (isTestMode()) throw new Error("Test subscription resets are disabled in UI test scenarios.");
    if (nativeStore() !== "apple" || this.premium.value.billingEnvironment !== "Sandbox")
      throw new Error("Test subscription reset is available only in the Apple sandbox app.");
    const auth = authState.value;
    if (auth.status !== "signedIn") throw new Error("Please sign in to reset your test subscription.");
    const playerId = auth.identity.playerId;
    const generation = this._sessionGeneration;
    let stage = "resetting-sandbox";
    recordDiagnosticEvent("subscription-management", { stage, billingEnvironment: "Sandbox" });
    try {
      const response = await this._callWithBearer((bearer) => {
        if (!this._isCurrentPlayer(playerId, generation))
          throw new Error("Your account changed. Please try again.");
        return storeBillingRequest<{ reset: boolean }>(bearer, "reset-sandbox", { store: "apple" });
      });
      if (response.result !== "Success")
        throw new Error(
          response.result === "Error"
            ? response.message
            : "Please sign in again to reset your test subscription.",
        );
      if (response.data?.reset !== true)
        throw new Error("The test subscription reset was not confirmed. Please try again.");
      if (!this._isCurrentPlayer(playerId, generation))
        throw new Error("Your account changed during the test subscription reset.");
      stage = "refreshing-after-reset";
      this._invalidateCachedPremiumStatus(playerId);
      const outcome = await this.forcePremiumAccessRefresh();
      if (!this._isCurrentPlayer(playerId, generation))
        throw new Error("Your account changed during the test subscription reset.");
      recordDiagnosticEvent("subscription-management", {
        stage, outcome, billingEnvironment: "Sandbox",
      });
      if (outcome === "error")
        throw new Error("Test subscription reset, but access could not refresh. Please try again.");
      return "Test subscription reset. Restore Purchases can bring back active test access. Apple purchase history is unchanged; clear it in Sandbox Account settings before buying again.";
    } catch (error) {
      recordDiagnosticEvent("subscription-management", {
        stage, outcome: "failed", error, billingEnvironment: "Sandbox",
      });
      throw error;
    }
  }

  public async loadStorePlans(): Promise<{ plans: StorePlan[]; accountToken: string }> {
    const store = nativeStore();
    if (!store) throw new Error("Purchase premium in the Wirder iOS or Android app.");
    const response = await this._callWithBearer((bearer) =>
      storeBillingRequest<{ products: StoreProduct[]; accountToken: string }>(bearer, "catalog", {
        store,
      }),
    );
    if (response.result !== "Success") {
      throw new Error(
        response.result === "Error"
          ? response.message
          : "Please sign in again to view subscriptions.",
      );
    }
    const { products, accountToken } = response.data;
    if (!products.length)
      throw new Error(
        "No subscriptions are configured for this store. Please contact Wirder support. (STORE_CATALOG_EMPTY)",
      );
    const available = await billingPlugin().getProducts({ products });
    const plans = matchStorePlans(products, available.products);
    if (!plans.length) {
      const counts = `requested ${products.length}, received ${available.products.length}`;
      if (!available.products.length)
        throw new Error(
          `The app received no subscription offers from ${
            store === "apple" ? "the Apple App Store" : "Google Play"
          }. Please try again later. (STORE_OFFERS_EMPTY: ${counts})`,
        );
      throw new Error(
        `The store returned subscription offers, but none matched Wirder's configured plans. Please contact Wirder support. (STORE_OFFERS_UNMATCHED: ${counts})`,
      );
    }
    return { plans, accountToken };
  }

  private async _verifyStoreTransactions(
    transactions: StoreTransaction[],
    playerId: string,
    sessionGeneration: number,
    explicitActivation = false,
  ): Promise<void> {
    const store = nativeStore();
    for (const transaction of transactions) {
      if (!this._isCurrentPlayer(playerId, sessionGeneration))
        throw new Error(
          "Your account changed. Restore purchases while signed in to the account used to subscribe.",
        );
      const response = await this._callWithBearer((bearer) =>
        storeBillingRequest<{ verified: boolean }>(bearer, explicitActivation ? "restore" : "verify", {
          store, ...transaction,
        }),
      );
      if (response.result !== "Success") {
        throw new Error(
          response.result === "Error"
            ? response.message
            : "Please sign in again to verify your subscription.",
        );
      }
      if (!response.data.verified) throw new Error("The store purchase could not be verified.");
      if (store === "apple" && transaction.transactionId) {
        await billingPlugin().finish({ transactionId: transaction.transactionId });
      }
    }
    if (!this._isCurrentPlayer(playerId, sessionGeneration))
      throw new Error("Your account changed during subscription verification.");
    this._invalidateCachedPremiumStatus(playerId);
    const outcome = await this.forcePremiumAccessRefresh();
    if (outcome === "error")
      throw new Error(
        "Your purchase was verified, but premium access could not refresh. Please check again in Settings.",
      );
  }

  public purchaseStorePlan(plan: StorePlan): Promise<string> {
    if (this._storeOperation) return this._storeOperation;
    const operation = this._purchaseStorePlan(plan);
    this._storeOperation = operation;
    void operation.then(
      () => {
        this._storeOperation = null;
      },
      () => {
        this._storeOperation = null;
      },
    );
    return operation;
  }

  private async _purchaseStorePlan(plan: StorePlan): Promise<string> {
    if (isTestMode()) throw new Error("Store purchases are disabled in test scenarios.");
    const auth = authState.value;
    if (auth.status !== "signedIn") throw new Error("Please sign in to subscribe.");
    const playerId = auth.identity.playerId;
    const generation = this._sessionGeneration;
    const catalog = await this.loadStorePlans();
    if (!this._isCurrentPlayer(playerId, generation))
      throw new Error("Your account changed. Please try again.");
    // Recheck shared access before opening a second store's checkout.
    await this.loadPremiumStatus(false);
    if (!this._isCurrentPlayer(playerId, generation))
      throw new Error("Your account changed. Please try again.");
    if (this.premium.value.status !== "ready" || this.premium.value.error)
      throw new Error("Could not check existing subscriptions. Please try again.");
    if (this.premium.value.subscription)
      throw new Error("You already have a subscription. Use Manage Subscription to make changes.");
    const currentPlan = catalog.plans.find(
      (item) => item.productId === plan.productId && item.basePlanId === plan.basePlanId,
    );
    if (!currentPlan)
      throw new Error("This plan is no longer available. Please reload subscriptions.");
    const purchase = await billingPlugin().purchase({
      ...currentPlan,
      accountToken: catalog.accountToken,
    });
    if (purchase.state === "cancelled") return "Purchase cancelled. You have not been charged.";
    if (purchase.state === "pending")
      return "Your purchase is awaiting store approval. Premium will unlock after it is approved.";
    if (!purchase.transactions.length)
      throw new Error("The store returned no purchase. Use Restore Purchases to check.");
    await this._verifyStoreTransactions(purchase.transactions, playerId, generation, true);
    if (!this.premium.value.subscription)
      throw new Error(
        "Your store purchase was checked, but no active subscription was found. Use Restore Purchases before trying to buy again.",
      );
    return "Your subscription has been verified.";
  }

  public async restoreStorePurchases(sync = true): Promise<string> {
    if (this._storeOperation) return this._storeOperation;
    const operation = this._restoreStorePurchases(sync);
    this._storeOperation = operation;
    void operation.then(
      () => {
        this._storeOperation = null;
      },
      () => {
        this._storeOperation = null;
      },
    );
    return operation;
  }

  private async _restoreStorePurchases(sync: boolean): Promise<string> {
    if (isTestMode()) throw new Error("Store purchases are disabled in test scenarios.");
    const auth = authState.value;
    if (auth.status !== "signedIn") throw new Error("Please sign in to restore purchases.");
    const playerId = auth.identity.playerId;
    const generation = this._sessionGeneration;
    const restored = await billingPlugin().restorePurchases({ sync });
    if (!this._isCurrentPlayer(playerId, generation))
      throw new Error("Your account changed. Please try again.");
    if (restored.transactions.length)
      await this._verifyStoreTransactions(restored.transactions, playerId, generation, sync);
    else if (sync) {
      if (!this._isCurrentPlayer(playerId, generation))
        throw new Error("Your account changed. Please try again.");
      if ((await this.forcePremiumAccessRefresh()) === "error") {
        throw new Error("Could not check premium access. Please try again.");
      }
    }
    if (!this._isCurrentPlayer(playerId, generation))
      throw new Error("Your account changed. Please try again.");
    this.storeRecoveryError.value = null;
    return restored.transactions.length
      ? this.premium.value.subscription
        ? "Your store purchases have been restored."
        : "Your store purchases were checked. No active subscription was found in the restored purchases."
      : sync && this.premium.value.tier
        ? "Your premium access is up to date."
        : "No purchases were found in this store account.";
  }

  /** A Patreon membership link just opened outside the app. Ensure its next
   * genuine return is not suppressed by an unrelated recent focus event. */
  public notePatreonDeparture(): void {
    const auth = authState.value;
    if (auth.status === "signedIn") {
      this._invalidateCachedPremiumStatus(auth.identity.playerId);
      this._setPatreonPurchaseIntent(auth.identity.playerId);
    }
    this._lastPremiumResumeRefreshAt = 0;
  }

  private _premiumRefreshOutcome(): PremiumRefreshOutcome {
    if (this.premium.value.error) return "error";
    return (
      this.patreonPurchaseIntent.value ? this.premium.value.patreon.tier : this.premium.value.tier
    )
      ? "active"
      : "noTier";
  }

  private _handlePatreonPurchaseRefresh(
    playerId: string,
    sessionGeneration: number,
    screen: Screen,
    outcome: PremiumRefreshOutcome,
    allowRetry = true,
  ): void {
    if (!this.patreonPurchaseIntent.value || !this._isCurrentPlayer(playerId, sessionGeneration)) {
      return;
    }
    if (outcome === "active") {
      this._clearPatreonPurchaseIntent(playerId);
      return;
    }
    if (
      outcome === "noTier" &&
      this.premium.value.status === "ready" &&
      !this.premium.value.patreon.linked
    ) {
      this._cancelPatreonPurchaseRetryTimer();
      this.patreonPurchaseState.value = "waiting";
      return;
    }
    this.patreonPurchaseState.value = outcome === "error" ? "error" : "waiting";
    if (!allowRetry || this.currentScreen.value !== screen) return;
    this._schedulePatreonPurchaseRetry(playerId, sessionGeneration, screen, outcome);
  }

  private _purchaseRetryDelays(): readonly number[] {
    return isTestMode() && this._testPurchaseRetryDelaysMs
      ? this._testPurchaseRetryDelaysMs
      : PATREON_PURCHASE_RETRY_DELAYS_MS;
  }

  private _schedulePatreonPurchaseRetry(
    playerId: string,
    sessionGeneration: number,
    screen: Screen,
    lastOutcome: PremiumRefreshOutcome,
  ): void {
    const delays = this._purchaseRetryDelays();
    if (this._patreonPurchaseRetryAttempt >= delays.length) {
      this.patreonPurchaseState.value = lastOutcome === "error" ? "errorExhausted" : "exhausted";
      return;
    }
    this._cancelPatreonPurchaseRetryTimer();
    const retryGeneration = this._patreonPurchaseRetryGeneration;
    const delay = delays[this._patreonPurchaseRetryAttempt++];
    this._patreonPurchaseRetryScreen = screen;
    this._patreonPurchaseRetryTimer = setTimeout(async () => {
      this._patreonPurchaseRetryTimer = undefined;
      if (
        retryGeneration !== this._patreonPurchaseRetryGeneration ||
        document.visibilityState === "hidden" ||
        this.currentScreen.value !== screen ||
        !this._isCurrentPlayer(playerId, sessionGeneration) ||
        !this.patreonPurchaseIntent.value
      ) {
        return;
      }
      this.patreonPurchaseState.value = "checking";
      let outcome: PremiumRefreshOutcome = "error";
      try {
        await this._runPremiumAccessRefresh();
        outcome = this._premiumRefreshOutcome();
      } finally {
        this._handlePatreonPurchaseRefresh(
          playerId,
          sessionGeneration,
          screen,
          outcome,
          retryGeneration === this._patreonPurchaseRetryGeneration,
        );
      }
    }, delay);
  }

  private _cancelPatreonPurchaseRetryTimer(): void {
    this._patreonPurchaseRetryGeneration++;
    if (this._patreonPurchaseRetryTimer !== undefined) {
      clearTimeout(this._patreonPurchaseRetryTimer);
      this._patreonPurchaseRetryTimer = undefined;
    }
    this._patreonPurchaseRetryScreen = undefined;
  }

  private _resetPatreonPurchaseRetries(): void {
    this._cancelPatreonPurchaseRetryTimer();
    this._patreonPurchaseRetryAttempt = 0;
  }

  private _restorePatreonPurchaseIntent(playerId: string): void {
    let active = false;
    try {
      const key = playerSessionKey(PATREON_PURCHASE_INTENT_KEY, playerId);
      const startedAt = Number(sessionStorage.getItem(key));
      active =
        Number.isFinite(startedAt) &&
        startedAt > 0 &&
        Date.now() - startedAt <= PATREON_PURCHASE_INTENT_MAX_AGE_MS;
      if (!active) sessionStorage.removeItem(key);
    } catch {
      active = false;
    }
    if (active) {
      this.patreonPurchaseIntent.value = true;
      if (this.patreonPurchaseState.value === "idle") {
        this.patreonPurchaseState.value = "waiting";
      }
    } else {
      this._clearPatreonPurchaseIntent(playerId);
    }
  }

  private _setPatreonPurchaseIntent(playerId: string): void {
    this._resetPatreonPurchaseRetries();
    this.patreonPurchaseIntent.value = true;
    this.patreonPurchaseState.value = "waiting";
    try {
      sessionStorage.setItem(
        playerSessionKey(PATREON_PURCHASE_INTENT_KEY, playerId),
        String(Date.now()),
      );
    } catch {
      // In-memory intent still covers the current app lifetime.
    }
  }

  private _clearPatreonPurchaseIntent(playerId: string): void {
    this._resetPatreonPurchaseRetries();
    const auth = authState.value;
    if (auth.status === "signedIn" && auth.identity.playerId === playerId) {
      this.patreonPurchaseIntent.value = false;
      this.patreonPurchaseState.value = "idle";
    }
    try {
      sessionStorage.removeItem(playerSessionKey(PATREON_PURCHASE_INTENT_KEY, playerId));
    } catch {
      // The in-memory state is already clear for the current player.
    }
  }

  private _setPatreonPurchaseIntentForTest(playerId: string, active: boolean): void {
    if (active) {
      this._setPatreonPurchaseIntent(playerId);
    } else {
      this._clearPatreonPurchaseIntent(playerId);
    }
  }

  private _recognizePaidPremiumTier(
    playerId: string,
    tier: PremiumTier | null,
    environment: PremiumStatus["billingEnvironment"] = "Production",
  ): void {
    const previous = this._confirmedPaidMembership;
    const current = { playerId, tier, environment: environment ?? "Production" };
    this._confirmedPaidMembership = current;
    if (tier) this._clearPatreonPurchaseIntent(playerId);
    if (!previous || previous.playerId !== playerId || previous.environment !== current.environment) {
      this.premiumCelebration.value = null;
      return;
    }
    if (!tier || premiumTierRank(tier) <= premiumTierRank(previous.tier)) {
      if (previous.tier !== tier) this.premiumCelebration.value = null;
      return;
    }
    this.premiumCelebration.value = { tier, previousTier: previous.tier };
  }

  /** Leave Wirder for Patreon's server-authorized account-linking flow. */
  public async beginPatreonLink(continueToMembership = false): Promise<FriendActionResult> {
    if (isTestMode()) {
      const auth = authState.value;
      const playerId = auth.status === "signedIn" ? auth.identity.playerId : TEST_SELF_ID;
      this._invalidateCachedPremiumStatus(playerId);
      this.premium.value = {
        ...this.premium.value,
        status: "ready",
        tier: "gold",
        source: "patreon",
        patreon: {
          configured: true,
          linked: true,
          tier: "gold",
          displayName: "Test Patron",
          membershipStatus: "active_patron",
        },
      };
      this._recognizePaidPremiumTier(playerId, "gold");
      return "Success";
    }
    const auth = authState.value;
    if (auth.status !== "signedIn") return "NotSignedIn";
    const playerId = auth.identity.playerId;
    this._invalidateCachedPremiumStatus(playerId);
    const generation = this._sessionGeneration;
    try {
      const key = playerSessionKey(PENDING_PATREON_MEMBERSHIP_KEY, playerId);
      if (continueToMembership) {
        sessionStorage.setItem(key, "1");
      } else {
        sessionStorage.removeItem(key);
      }
    } catch {
      if (continueToMembership) return "Error";
    }
    const transactionProof = generateBrowserTransactionProof();
    this._pendingPatreonProof = transactionProof;
    this._pendingPatreonPlayerId = playerId;
    try {
      sessionStorage.setItem(
        playerSessionKey(PENDING_PATREON_PROOF_KEY, playerId),
        transactionProof,
      );
    } catch {
      // The in-memory proof still works if this navigation keeps the page alive.
    }
    const response = await this._callWithBearer((bearer) =>
      startPatreonLink(bearer, transactionProof, window.location.origin),
    );
    if (!this._isCurrentPlayer(playerId, generation)) return "NotSignedIn";
    if (response.result === "Success") {
      window.location.assign(response.authorizationUrl);
      return "Success";
    }
    this._clearPendingPatreonTransaction(playerId);
    return this._mapActionResult(response.result);
  }

  /** Remove the server-side Patreon link and refresh the local access state. */
  public async unlinkPatreon(): Promise<FriendActionResult> {
    if (isTestMode()) {
      const auth = authState.value;
      if (auth.status === "signedIn") {
        this._invalidateCachedPremiumStatus(auth.identity.playerId);
      }
      this.premium.value = {
        ...EMPTY_PREMIUM_STATE,
        generation: this._premiumDataGeneration.start(),
        status: "ready",
        patreon: { ...EMPTY_PREMIUM_STATE.patreon, configured: true },
      };
      return "Success";
    }
    const response = await this._callWithBearer((bearer) => unlinkPatreon(bearer));
    const result = this._mapActionResult(response.result);
    if (result === "Success") {
      const auth = authState.value;
      if (auth.status === "signedIn") {
        this._invalidateCachedPremiumStatus(auth.identity.playerId);
      }
      await this.loadPremiumStatus(false);
    }
    return result;
  }

  public clearPremiumNotice(): void {
    this.premiumNotice.value = null;
  }

  public clearPremiumCelebration(): void {
    this.premiumCelebration.value = null;
  }

  /** Capture and remove the staged Patreon callback result from the URL. The
   * authenticated load path completes it only after Wirder identity is known. */
  public capturePremiumCallbackNotice(rawUrl?: string): boolean {
    const callback = readPremiumCallback(rawUrl);
    this.premiumNotice.value = callback.notice ? { message: callback.notice } : null;
    const auth = authState.value;
    const playerId = auth.status === "signedIn" ? auth.identity.playerId : null;
    if (!playerId) {
      if (callback.resultId) {
        this.premiumNotice.value = {
          message: "Sign in to the Wirder account that started this Patreon link, then try again.",
        };
      }
      return false;
    }
    this._pendingPatreonPlayerId = playerId;
    if (callback.resultId) {
      try {
        this._pendingPatreonProof = sessionStorage.getItem(
          playerSessionKey(PENDING_PATREON_PROOF_KEY, playerId),
        );
      } catch {
        this._pendingPatreonProof = null;
      }
      if (this._pendingPatreonProof) {
        this._pendingPatreonResult = callback.resultId;
        try {
          sessionStorage.setItem(
            playerSessionKey(PENDING_PATREON_RESULT_KEY, playerId),
            callback.resultId,
          );
        } catch {
          // The in-memory copy still completes during this page load.
        }
      } else {
        this.premiumNotice.value = {
          message:
            "This Patreon callback was not started in this browser session. No account was linked.",
        };
      }
    } else {
      try {
        this._pendingPatreonResult = sessionStorage.getItem(
          playerSessionKey(PENDING_PATREON_RESULT_KEY, playerId),
        );
        this._pendingPatreonProof = sessionStorage.getItem(
          playerSessionKey(PENDING_PATREON_PROOF_KEY, playerId),
        );
      } catch {
        this._pendingPatreonResult = null;
        this._pendingPatreonProof = null;
      }
    }
    return callback.resultId !== null;
  }

  /** Complete a proof-bound Patreon callback delivered by Capacitor rather than
   * through the WebView's current URL. */
  public async handleIncomingPatreonCallback(rawUrl: string): Promise<void> {
    if (!this.capturePremiumCallbackNotice(rawUrl)) return;
    const auth = authState.value;
    if (auth.status !== "signedIn") return;
    await this.handleAppResume();
  }

  private async _finalizePendingPatreonLink(
    playerId: string,
    generation: number,
  ): Promise<boolean> {
    if (this._pendingPatreonFinalizeRequest) {
      return this._pendingPatreonFinalizeRequest;
    }
    const request = this._finalizePendingPatreonLinkCore(playerId, generation);
    this._pendingPatreonFinalizeRequest = request;
    try {
      return await request;
    } finally {
      if (this._pendingPatreonFinalizeRequest === request) {
        this._pendingPatreonFinalizeRequest = null;
      }
    }
  }

  private async _finalizePendingPatreonLinkCore(
    playerId: string,
    generation: number,
  ): Promise<boolean> {
    const resultId = this._pendingPatreonResult;
    const transactionProof = this._pendingPatreonProof;
    if (
      !resultId ||
      !transactionProof ||
      this._pendingPatreonPlayerId !== playerId ||
      !this._isCurrentPlayer(playerId, generation)
    )
      return false;

    const response = await this._callWithBearer((bearer) =>
      finalizePatreonLink(bearer, resultId, transactionProof),
    );
    if (!this._isCurrentPlayer(playerId, generation)) return false;
    let continueToMembership = false;
    try {
      continueToMembership =
        sessionStorage.getItem(playerSessionKey(PENDING_PATREON_MEMBERSHIP_KEY, playerId)) === "1";
    } catch {
      continueToMembership = false;
    }
    const finalized = response.result === "Success";
    if (response.result === "Success") {
      this._invalidateCachedPremiumStatus(playerId);
      this._recognizePaidPremiumTier(playerId, response.tier);
      if (response.tier) {
        this.premiumNotice.value = {
          message: `Patreon linked — your ${response.tier[0].toUpperCase()}${response.tier.slice(
            1,
          )} backer extras are ready.`,
        };
      } else if (continueToMembership) {
        this._setPatreonPurchaseIntent(playerId);
        this.premiumNotice.value = null;
      } else {
        this.premiumNotice.value = {
          message: "Patreon is linked, but a Wirder membership wasn't found yet.",
          action: "join",
        };
      }
    } else if (response.result === "AlreadyLinked") {
      this.premiumNotice.value = {
        message: "That Patreon account is already linked to another Wirder account.",
      };
    } else if (response.result === "Expired" || response.result === "Stale") {
      this.premiumNotice.value = {
        message: "That Patreon link expired or was superseded. Please try again.",
      };
    } else {
      this.premiumNotice.value = {
        message: "Patreon linking didn't finish. Please try again.",
      };
    }
    this._clearPendingPatreonTransaction(playerId);
    if (finalized && !response.tier && continueToMembership) {
      window.location.assign(PATREON_JOIN_URL);
    }
    return finalized;
  }

  private _clearPendingPatreonTransaction(playerId?: string): void {
    const owner = playerId ?? this._pendingPatreonPlayerId;
    this._pendingPatreonResult = null;
    this._pendingPatreonProof = null;
    this._pendingPatreonPlayerId = null;
    try {
      if (owner) {
        sessionStorage.removeItem(playerSessionKey(PENDING_PATREON_RESULT_KEY, owner));
        sessionStorage.removeItem(playerSessionKey(PENDING_PATREON_PROOF_KEY, owner));
        sessionStorage.removeItem(playerSessionKey(PENDING_PATREON_MEMBERSHIP_KEY, owner));
      }
    } catch {
      // Nothing else to clean up.
    }
  }

  private _isCurrentPlayer(playerId: string, generation: number): boolean {
    const current = authState.value;
    return (
      generation === this._sessionGeneration &&
      current.status === "signedIn" &&
      current.identity.playerId === playerId
    );
  }

  /**
   * Run an authenticated social mutation with the same bearer-token strategy as
   * submitPuzzle/submitFeedback: prefer the cached session JWT, fall back to a
   * fresh provider token, and retry once if the server rejects the session token
   * as expired. Returns `{ result: "NotSignedIn" }` if no identity is available.
   */
  private async _callWithBearer<T extends { result: string }>(
    fn: (bearer: string) => Promise<T>,
  ): Promise<T | { result: "NotSignedIn" }> {
    if (isTestMode()) {
      // Callers short-circuit test mode before reaching here, but guard anyway.
      return { result: "NotSignedIn" };
    }
    const auth = authState.value;
    if (auth.status !== "signedIn") {
      return { result: "NotSignedIn" };
    }

    const playerId = auth.identity.playerId;
    const generation = this._sessionGeneration;
    const isCurrent = () => this._isCurrentPlayer(playerId, generation);
    const acquireFreshProviderToken = async (): Promise<string> => {
      if (!isCurrent()) throw new Error("Authenticated operation was superseded");
      const token = await withTimeout(
        auth.identity.provider === "apple"
          ? appleAuth.requestFreshToken()
          : googleAuth.requestFreshToken(),
        AUTH_REFRESH_TIMEOUT_MS,
        "Timed out while refreshing authentication",
      );
      if (!isCurrent()) throw new Error("Authenticated operation was superseded");
      return token;
    };

    const cachedSessionToken = getSessionToken(playerId);
    let bearer: string;
    let usingSessionToken: boolean;
    if (cachedSessionToken) {
      bearer = cachedSessionToken;
      usingSessionToken = true;
    } else {
      try {
        bearer = await acquireFreshProviderToken();
      } catch (err) {
        console.error("Failed to obtain fresh ID token for social action:", err);
        return { result: "NotSignedIn" };
      }
      usingSessionToken = false;
    }

    if (!isCurrent()) return { result: "NotSignedIn" };
    let res = await fn(bearer);
    if (!isCurrent()) return { result: "NotSignedIn" };
    if (res.result === "Unauthorized" && usingSessionToken) {
      clearSessionToken(playerId);
      if (!isCurrent()) return { result: "NotSignedIn" };
      try {
        bearer = await acquireFreshProviderToken();
      } catch (err) {
        console.error("Session token rejected and social re-auth failed:", err);
        return { result: "NotSignedIn" };
      }
      if (!isCurrent()) return { result: "NotSignedIn" };
      res = await fn(bearer);
      if (!isCurrent()) return { result: "NotSignedIn" };
    }
    return res;
  }

  /** Collapse a backend action result string into the UI-facing enum. */
  private _mapActionResult(result: string): FriendActionResult {
    switch (result) {
      case "Success":
        return "Success";
      case "NotSignedIn":
      case "Unauthorized":
        return "NotSignedIn";
      case "NotFound":
        return "NotFound";
      case "Expired":
        return "Expired";
      case "AlreadyUsed":
        return "AlreadyUsed";
      case "RateLimited":
        return "RateLimited";
      default:
        return "Error";
    }
  }

  private async _fetchPuzzleForDate(
    date: SimpleDate,
    options?: { cacheBust?: boolean },
  ): Promise<FetchPuzzleResult> {
    const auth = authState.value;
    const playerId = auth.status === "signedIn" ? auth.identity.playerId : this._session?.userGuid;
    return playerId
      ? fetchPuzzleForDate(date, playerId, options)
      : { result: "Error", message: "No active player" };
  }

  /** The signed-in player's own id, from auth if present else the session. */
  public get selfPlayerId(): string | undefined {
    const auth = authState.value;
    if (auth.status === "signedIn") return auth.identity.playerId;
    return this._session?.userGuid;
  }

  /**
   * (Re)load the friend roster from the CDN using the cached rosterToken. Safe to
   * call any time; no-ops in test mode and clears the roster if signed out. Pass
   * `cacheBust` right after a mutation to bypass a stale edge-cached projection.
   */
  public async loadRoster(options?: { cacheBust?: boolean }): Promise<void> {
    if (isTestMode()) {
      // Offline fixture: seed a canned roster once, then leave it to the
      // in-memory mutations below (so a demo's edits survive a re-open).
      if (!this.roster.value) {
        this.roster.value = buildTestRoster();
      }
      return;
    }
    const auth = authState.value;
    if (auth.status !== "signedIn") {
      this.roster.value = null;
      return;
    }
    const playerId = auth.identity.playerId;
    const generation = this._sessionGeneration;
    const rosterToken = getRosterToken(playerId);
    if (!rosterToken) {
      // No cached roster token. This is the normal state for a player who
      // signed in before the friend leaderboard shipped, or who is on a second
      // device — /api/me only hands out the token during interactive sign-in,
      // and the app boots from a cached session token without re-calling it.
      // Recover it silently from the session bearer (no sign-in prompt), then
      // continue. If we can't, leave the roster empty as before.
      const recovered = await this._recoverRosterToken(playerId, generation);
      if (!this._isCurrentPlayer(playerId, generation)) return;
      if (!recovered) {
        this.roster.value = null;
        return;
      }
    }
    const token = getRosterToken(playerId);
    if (!token) {
      this.roster.value = null;
      return;
    }
    this.rosterLoading.value = true;
    try {
      const roster = await fetchRoster(token, options);
      if (!this._isCurrentPlayer(playerId, generation)) return;
      this.roster.value = roster ?? null;
    } finally {
      if (this._isCurrentPlayer(playerId, generation)) {
        this.rosterLoading.value = false;
      }
    }
  }

  /**
   * Fetch and cache the signed-in player's rosterToken from the backend using
   * the cached session token. No-op (returns false) when not signed in or when
   * no session token is cached, so app boot never triggers a sign-in prompt.
   * The token is stable, so this only does real work once per device.
   */
  private async _recoverRosterToken(playerId: string, generation: number): Promise<boolean> {
    if (isTestMode()) return false;
    if (!this._isCurrentPlayer(playerId, generation)) return false;
    // Only attempt with a cached session token to guarantee no interactive
    // prompt on boot. A player without a session token will pick up the roster
    // token the next time they sign in interactively (/api/me returns it).
    const sessionToken = getSessionToken(playerId);
    if (!sessionToken) return false;
    try {
      const res = await fetchSelfSocial(sessionToken);
      if (!this._isCurrentPlayer(playerId, generation)) return false;
      if (res.result === "Success") {
        setRosterToken(playerId, res.rosterToken);
        return true;
      }
      if (res.result === "Unauthorized") {
        // Stale session token: drop it so a future mutation re-auths cleanly.
        clearSessionToken(playerId);
      }
    } catch (err) {
      console.error("Failed to recover roster token:", err);
    }
    return false;
  }

  /** Create a single-use invite link; returns a ready-to-share URL on success.
   *  `from` is your name the invitee will see; `to` is your private label for
   *  them (what you'll see on your leaderboard). */
  public async createFriendInvite(
    from: string,
    to: string,
  ): Promise<{
    result: FriendActionResult;
    code?: string;
    url?: string;
    expiresAt?: string;
  }> {
    if (isTestMode()) {
      // Offline: mint a fake code and record it as an outstanding invite so the
      // "Invites you sent" list + Cancel flow are demoable without a backend.
      const code = `test-invite-${Math.random().toString(36).slice(2, 10)}`;
      const cur = this.roster.value ?? buildTestRoster();
      this.roster.value = {
        ...cur,
        // "From" is saved as the display-name default, mirroring the backend.
        self: { ...cur.self, displayName: from.trim() || cur.self.displayName },
        outgoing: [
          ...cur.outgoing,
          {
            code,
            createdAt: new Date().toISOString(),
            expiresAt: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
            toLabel: to.trim() || undefined,
          },
        ],
      };
      return {
        result: "Success",
        code,
        url: `${window.location.origin}/?invite=${code}`,
      };
    }
    const res = await this._callWithBearer((b) => createInvite(b, from, to));
    if (res.result === "Success") {
      // The invite is ready; a slow CDN must not hold up the share sheet.
      void this.loadRoster({ cacheBust: true }).catch((err) => {
        console.error("Failed to refresh roster after creating an invite:", err);
      });
      return {
        result: "Success",
        code: res.code,
        url: `${window.location.origin}/?invite=${res.code}`,
        expiresAt: res.expiresAt,
      };
    }
    return { result: this._mapActionResult(res.result) };
  }

  /** Accept an invite by code, forming a mutual friendship. `name` is the name
   *  you'll see for the inviter on your leaderboard (defaults to their "From"). */
  public async acceptFriendInvite(code: string, name?: string): Promise<FriendActionResult> {
    if (isTestMode()) {
      // Offline: fold the pending invite into the roster as a new friend, using
      // the name you chose (or the preview's name) so the deep-link accept flow
      // is visibly wired end-to-end.
      const cur = this.roster.value ?? buildTestRoster();
      const chosen =
        name?.trim() || this.pendingInvite.value?.preview?.fromDisplayName || "New Friend";
      const newFriend = {
        playerId: `friend-${code.slice(0, 12)}`,
        displayName: chosen,
        since: new Date().toISOString(),
      };
      if (!cur.friends.some((f) => f.playerId === newFriend.playerId)) {
        this.roster.value = { ...cur, friends: [...cur.friends, newFriend] };
      }
      this.clearPendingInvite();
      return "Success";
    }
    const res = await this._callWithBearer((b) => acceptInvite(b, code, name));
    const mapped = res.result === "Invalid" ? "Error" : this._mapActionResult(res.result);
    if (res.result === "Success") {
      this.clearPendingInvite();
      await this.loadRoster({ cacheBust: true });
    }
    return mapped;
  }

  /** Revoke one of your own outstanding invites. */
  public async cancelFriendInvite(code: string): Promise<FriendActionResult> {
    if (isTestMode()) {
      const cur = this.roster.value;
      if (cur) {
        this.roster.value = { ...cur, outgoing: cur.outgoing.filter((o) => o.code !== code) };
      }
      return "Success";
    }
    const res = await this._callWithBearer((b) => cancelInvite(b, code));
    if (res.result === "Success") await this.loadRoster({ cacheBust: true });
    return this._mapActionResult(res.result);
  }

  /** Unfriend a player (server removes both edges). */
  public async removeFriendById(friendId: string): Promise<FriendActionResult> {
    if (isTestMode()) {
      const cur = this.roster.value;
      if (cur) {
        this.roster.value = { ...cur, friends: cur.friends.filter((f) => f.playerId !== friendId) };
      }
      return "Success";
    }
    const res = await this._callWithBearer((b) => removeFriend(b, friendId));
    if (res.result === "Success") await this.loadRoster({ cacheBust: true });
    return this._mapActionResult(res.result);
  }

  /** Rename a friend — changes only the name you see for them on your board. */
  public async renameFriendById(friendId: string, name: string): Promise<FriendActionResult> {
    const trimmed = name.trim();
    if (!trimmed) return "Error";
    if (isTestMode()) {
      const cur = this.roster.value;
      if (cur) {
        this.roster.value = {
          ...cur,
          friends: cur.friends.map((f) =>
            f.playerId === friendId ? { ...f, displayName: trimmed } : f,
          ),
        };
      }
      return "Success";
    }
    const res = await this._callWithBearer((b) => renameFriend(b, friendId, trimmed));
    if (res.result === "Success") await this.loadRoster({ cacheBust: true });
    return this._mapActionResult(res.result);
  }

  /** Update the player's name without changing friends' private labels. */
  public async updateDisplayName(displayName: string): Promise<FriendActionResult> {
    const auth = authState.value;
    if (auth.status !== "signedIn") return "NotSignedIn";
    const playerId = auth.identity.playerId;
    const generation = this._sessionGeneration;
    const name = displayName
      .replace(/[\u0000-\u001f\u007f]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 30);
    if (!name) {
      console.error("Cannot save an empty display name");
      return "Error";
    }
    if (isTestMode()) {
      const cur = this.roster.value ?? buildTestRoster(auth.identity.name, playerId);
      this.roster.value = { ...cur, self: { ...cur.self, displayName: name } };
      return "Success";
    }
    const res = await this._callWithBearer((b) => setDisplayName(b, name));
    if (!this._isCurrentPlayer(playerId, generation)) return "NotSignedIn";
    if (res.result === "Success") {
      const previousRoster = this.roster.value;
      await this.loadRoster({ cacheBust: true });
      if (!this._isCurrentPlayer(playerId, generation)) return "NotSignedIn";
      // The mutation response is authoritative even if the CDN is stale or unavailable.
      const roster = this.roster.value ?? previousRoster;
      if (roster?.self.playerId === playerId) {
        this.roster.value = {
          ...roster,
          self: { ...roster.self, displayName: res.displayName },
        };
      }
    }
    return this._mapActionResult(res.result);
  }

  /**
   * Load the friend leaderboard for a puzzle date. The authenticated premium
   * check runs first; only an entitled viewer fans out score reads and ranks
   * them. Everyone else receives names-only rows built from the accepted roster.
   */
  public async loadLeaderboard(date: SimpleDate, automaticPremiumCheck = true): Promise<void> {
    const generation = isTestMode()
      ? this.premium.value.generation
      : this._premiumDataGeneration.start();
    const sessionGeneration = this._sessionGeneration;
    const authenticatedPlayerId =
      authState.value.status === "signedIn" ? authState.value.identity.playerId : undefined;
    const puzzleId = toPuzzleId(date);
    const roster = this.roster.value;
    const auth = authState.value;
    const selfId = this.selfPlayerId;

    const participants: { playerId: string; displayName: string; isSelf: boolean }[] = [];
    if (selfId) {
      const selfName =
        roster?.self.displayName ??
        (auth.status === "signedIn" ? auth.identity.name : undefined) ??
        "You";
      participants.push({ playerId: selfId, displayName: selfName, isSelf: true });
    }
    for (const friend of roster?.friends ?? []) {
      participants.push({
        playerId: friend.playerId,
        displayName: friend.displayName,
        isSelf: false,
      });
    }

    this.leaderboard.value = {
      generation,
      puzzleId,
      loading: true,
      scoresVisible: false,
      rows: [],
    };

    const isCurrent = (): boolean =>
      this._premiumDataGeneration.isCurrent(generation) &&
      sessionGeneration === this._sessionGeneration &&
      authenticatedPlayerId !== undefined &&
      this._isCurrentPlayer(authenticatedPlayerId, sessionGeneration) &&
      this.leaderboard.value?.puzzleId === puzzleId;

    const publishNamesOnly = (verificationFailed = false): void => {
      if (!isTestMode() && !isCurrent()) return;
      const tiers = this.premium.value.players;
      this.leaderboard.value = {
        generation,
        puzzleId,
        loading: false,
        scoresVisible: false,
        verificationFailed: verificationFailed || undefined,
        rows: participants.map((participant) => ({
          playerId: participant.playerId,
          displayName: participant.displayName,
          isSelf: participant.isSelf,
          tier: tiers[participant.playerId] ?? null,
          entry: {
            playerId: participant.playerId,
            played: false,
            scored: false,
            wordCount: 0,
            regularPoints: 0,
          },
        })),
      };
    };

    if (isTestMode()) {
      if (!this.premium.value.tier) {
        publishNamesOnly(this.premium.value.status === "error");
        return;
      }
      const isToday = areSimpleDatesEqual(date, getSimpleDateInSeattle());
      const rows = participants.map((participant) => ({
        playerId: participant.playerId,
        displayName: participant.displayName,
        isSelf: participant.isSelf,
        tier: this.premium.value.players[participant.playerId] ?? null,
        entry: buildTestLeaderboardEntry(puzzleId, participant.playerId, isToday),
      }));
      sortLeaderboardRows(rows);
      if (!this._premiumDataGeneration.isCurrent(generation)) return;
      this.leaderboard.value = {
        generation,
        puzzleId,
        loading: false,
        scoresVisible: true,
        rows,
      };
      return;
    }

    if (!authenticatedPlayerId) {
      const failureGeneration = this._premiumDataGeneration.invalidateIfCurrent(generation);
      if (failureGeneration !== undefined) {
        this.premium.value = { ...EMPTY_PREMIUM_STATE, generation: failureGeneration };
        this.leaderboard.value = {
          generation: failureGeneration,
          puzzleId,
          loading: false,
          scoresVisible: false,
          rows: participants.map((participant) => ({
            playerId: participant.playerId,
            displayName: participant.displayName,
            isSelf: participant.isSelf,
            tier: null,
            entry: {
              playerId: participant.playerId,
              played: false,
              scored: false,
              wordCount: 0,
              regularPoints: 0,
            },
          })),
        };
      }
      return;
    }

    const hasPremiumAccess = await this._loadPremiumStatusForGeneration(
      authenticatedPlayerId,
      sessionGeneration,
      generation,
      (failureGeneration) => {
        if (this.leaderboard.value?.puzzleId !== puzzleId) return;
        this.leaderboard.value = {
          generation: failureGeneration,
          puzzleId,
          loading: false,
          scoresVisible: false,
          verificationFailed: true,
          rows: participants.map((participant) => ({
            playerId: participant.playerId,
            displayName: participant.displayName,
            isSelf: participant.isSelf,
            tier: null,
            entry: {
              playerId: participant.playerId,
              played: false,
              scored: false,
              wordCount: 0,
              regularPoints: 0,
            },
          })),
        };
      },
      automaticPremiumCheck,
    );
    if (!isCurrent()) return;

    const friendTiers = await Promise.all(
      participants.map(
        async (participant) =>
          [
            participant.playerId,
            participant.isSelf
              ? this.premium.value.tier
              : await fetchPremiumProjection(participant.playerId),
          ] as const,
      ),
    );
    if (!isCurrent()) return;
    this.premium.value = {
      ...this.premium.value,
      players: Object.fromEntries(friendTiers),
    };

    if (!hasPremiumAccess) {
      publishNamesOnly();
      return;
    }

    const entries = await Promise.all(
      participants.map((participant) => fetchLeaderboardEntry(puzzleId, participant.playerId)),
    );
    if (!isCurrent()) return;
    const byPlayer = new Map(entries.map((entry) => [entry.playerId, entry]));
    const rows: LeaderboardRow[] = participants.map((participant) => ({
      playerId: participant.playerId,
      displayName: participant.displayName,
      isSelf: participant.isSelf,
      tier: this.premium.value.players[participant.playerId] ?? null,
      entry: byPlayer.get(participant.playerId) ?? {
        playerId: participant.playerId,
        played: false,
        scored: false,
        wordCount: 0,
        regularPoints: 0,
      },
    }));
    sortLeaderboardRows(rows);
    this.leaderboard.value = {
      generation,
      puzzleId,
      loading: false,
      scoresVisible: true,
      rows,
    };
  }

  /**
   * Open the FriendPlay screen for one leaderboard row and load that player's
   * found words for the given date. Navigates immediately (showing a loading
   * state) then fills in the play once it arrives. A late-arriving load for a
   * different player/date is ignored so it can't clobber a newer view.
   */
  public async viewFriendPlay(row: LeaderboardRow, date: SimpleDate): Promise<void> {
    if (!this.premium.value.tier) return;
    const generation = ++this._friendPlayLoadGeneration;
    const sessionGeneration = this._sessionGeneration;
    const playerId =
      authState.value.status === "signedIn" ? authState.value.identity.playerId : undefined;
    const puzzleId = toPuzzleId(date);
    const ownPuzzle = this.findPuzzleByDate(date);
    const ownWords = ownPuzzle?.foundWords.value.map((word) => word.word) ?? [];
    this.friendPlay.value = {
      playerId: row.playerId,
      displayName: row.displayName,
      isSelf: row.isSelf,
      date,
      puzzleId,
      ownWords,
      selfPlayed: ownPuzzle?.played ?? false,
      loading: true,
      error: false,
      play: null,
    };
    this.navigateTo("FriendPlay");

    let play: PlayerPlay | null = null;
    let error = false;
    if (isTestMode()) {
      const isToday = areSimpleDatesEqual(date, getSimpleDateInSeattle());
      play = buildTestPlayerPlay(puzzleId, row.playerId, isToday);
    } else {
      const result = await fetchPlayerPlay(date, row.playerId);
      if (result.result === "Success") {
        play = result.play;
      } else {
        error = true;
      }
    }

    // Ignore if the user has since navigated to a different player/date.
    const current = this.friendPlay.value;
    if (
      generation !== this._friendPlayLoadGeneration ||
      sessionGeneration !== this._sessionGeneration ||
      (playerId !== undefined && !this._isCurrentPlayer(playerId, sessionGeneration)) ||
      !current ||
      current.playerId !== row.playerId ||
      current.puzzleId !== puzzleId
    )
      return;

    this.friendPlay.value = { ...current, loading: false, error, play };
  }

  /* ----- Pending invite (from an /i/{code} deep link) ----- */

  /** Stash an invite code captured from a deep link, to prompt after sign-in. */
  public setPendingInviteCode(code: string): void {
    try {
      sessionStorage.setItem(PENDING_INVITE_KEY, code);
    } catch {
      /* private mode / storage disabled — the code just won't survive reload. */
    }
    this.pendingInvite.value = { code, preview: null };
  }

  /** Drop any pending invite (accepted, declined, or expired). */
  public clearPendingInvite(): void {
    try {
      sessionStorage.removeItem(PENDING_INVITE_KEY);
    } catch {
      /* ignore */
    }
    this.pendingInvite.value = null;
  }

  /**
   * Surface an invite that arrived via a native iOS Universal Link while the app
   * was already open (Capacitor's App `appUrlOpen` event — see nativeDeepLink.ts).
   * The web/PWA path captures the code from `window.location` at boot instead;
   * this is the equivalent for the remote-loaded native shell, where the tapped
   * link never lands in `window.location`. Mirrors `_restorePendingInvite`: stash
   * the code, fetch its preview, and — once we're past the boot screens — jump to
   * the Friends screen where the accept banner lives. (During boot we leave the
   * landing to the connecting screen so there's a single routing path.)
   */
  public async handleIncomingInvite(code: string): Promise<void> {
    if (!/^[A-Za-z0-9_-]{16,64}$/.test(code)) return;
    this.setPendingInviteCode(code);
    const preview = await fetchInvitePreview(code, { cacheBust: true });
    if (preview && preview.status === "pending") {
      this.pendingInvite.value = { code, preview };
    } else {
      this.clearPendingInvite();
      return;
    }
    const screen = this.currentScreen.value;
    const booting = screen === "Loading" || screen === "Connecting";
    if (!booting && screen !== "Friends") {
      this.navigateTo("Friends");
    }
  }

  /**
   * On boot, if a deep-link invite code was stashed, fetch its public preview so
   * the Friends screen can show "X invited you — Accept?". Drops invites that are
   * no longer pending (expired / cancelled / already accepted).
   */
  private async _restorePendingInvite(): Promise<void> {
    let code: string | null = null;
    try {
      code = sessionStorage.getItem(PENDING_INVITE_KEY);
    } catch {
      code = null;
    }
    if (!code) return;
    this.pendingInvite.value = { code, preview: null };
    const preview = await fetchInvitePreview(code, { cacheBust: true });
    if (preview && preview.status === "pending") {
      this.pendingInvite.value = { code, preview };
    } else {
      this.clearPendingInvite();
    }
  }

  /**
   * Refresh puzzle data from the backend:
   * - Always fetch today's puzzle.
   * - Discover all dates the player has played (from CDN player stats'
   *   `puzzlesPlayed` field) and fetch those too — this lets a new device
   *   rebuild the full history without relying on localStorage.
   * - For past puzzles already in the local cache that haven't received
   *   solution scoring yet, also re-fetch them.
   */
  private async _refreshPuzzlesFromBackend(session: ISessionDataV1): Promise<"OK" | "Error"> {
    const today = getSimpleDateInSeattle();
    const datesToFetch: SimpleDate[] = [today];

    // Discover historical play dates from the existing public CDN stats file.
    const playedDates = await fetchPlayedDates(session.userGuid);
    for (const date of playedDates) {
      if (areSimpleDatesEqual(date, today)) continue;
      if (!datesToFetch.some((d) => areSimpleDatesEqual(d, date))) {
        datesToFetch.push(date);
      }
    }

    // Also re-fetch locally-cached puzzles that are still missing solution data
    // (e.g. yesterday's puzzle submitted after we already fetched playedDates),
    // or that the player finished but whose rare-word bonus hasn't been scored
    // yet. The latter happens when a puzzle is viewed in the window between the
    // solution being published and this player's scoredPlay appearing (rollover
    // in progress / a transient scoredPlay 404): the cache then holds the
    // solution but no rarityPoints, and without this it would stay stuck showing
    // a "0" rare-word bonus. Re-fetching lets the real score fill in.
    for (const puzzle of session.puzzles) {
      if (datesToFetch.some((d) => areSimpleDatesEqual(d, puzzle.date))) continue;
      const missingSolution =
        puzzle.solution.totalSubmissions === undefined || puzzle.solution.totalSubmissions === null;
      const playedButUnscored =
        puzzle.played && (puzzle.rarityPoints === undefined || puzzle.rarityPoints === null);
      if (missingSolution || playedButUnscored) {
        datesToFetch.push(puzzle.date);
      }
    }

    const results = await Promise.all(datesToFetch.map((date) => this._fetchPuzzleForDate(date)));

    let sawSuccess = false;
    let todayNotFound = false;
    for (let i = 0; i < results.length; i++) {
      const result = results[i];
      const date = datesToFetch[i];

      if (result.result === "Success") {
        sawSuccess = true;
        this._mergeRemotePuzzleIntoSession(session, result.puzzle);
      } else if (result.result === "NotFound") {
        if (areSimpleDatesEqual(date, today)) {
          todayNotFound = true;
          console.warn(`Today's puzzle ${toPuzzleId(date)} not published yet`);
        }
      } else {
        console.error(
          `Failed to fetch puzzle for ${toPuzzleId(date)}: ${
            result.result === "Unauthorized" ? "unauthorized" : result.message
          }`,
        );
      }
    }

    // Today's puzzle not being published yet is an expected state (handled by a
    // background rollover poke), not a load failure. Only treat the refresh as an
    // error when nothing succeeded AND it wasn't merely today's puzzle missing.
    if (!sawSuccess && !todayNotFound && results.length > 0) {
      return "Error";
    }
    return "OK";
  }

  /**
   * Background handler for a missing "today" puzzle: poke the server to run the
   * rollover, re-check the CDN with a fast backoff, and either merge the puzzle
   * in (flipping status back to "idle" so Home re-renders with the real card) or
   * show the "not ready" card and keep polling gently until it appears.
   */
  private async _pokeForTodaysPuzzleInBackground(playerId: string): Promise<void> {
    // Invalidate any prior poll and claim this generation.
    this._cancelTodayPolling();
    const generation = this._todayPollGeneration;
    const today = getSimpleDateInSeattle();

    try {
      const result = await pokeRolloverAndRefetch(today, playerId);
      if (this._todayPollGeneration !== generation) return; // superseded
      if (result.result === "Success") {
        this._mergeRemotePuzzleAndRebuild(result.puzzle);
        this.todayPuzzleStatus.value = "idle";
        return;
      }
    } catch (err) {
      console.error("Background rollover poke failed:", err);
      if (this._todayPollGeneration !== generation) return;
    }

    // Still not published. Surface the "not ready" card and keep checking in the
    // background so it appears on its own without any user action.
    this.todayPuzzleStatus.value = "notReady";
    this._scheduleTodayPoll(playerId, generation);
  }

  /** Schedule the next steady-state poll cycle for a not-yet-ready today puzzle. */
  private _scheduleTodayPoll(playerId: string, generation: number): void {
    if (this._todayPollGeneration !== generation) return;
    this._todayPollTimer = setTimeout(() => {
      void this._runTodayPollCycle(playerId, generation);
    }, TODAY_POLL_INTERVAL_MS);
  }

  /** One steady-state poll: re-poke + cache-busted CDN re-check, then reschedule. */
  private async _runTodayPollCycle(playerId: string, generation: number): Promise<void> {
    if (this._todayPollGeneration !== generation) return;
    const today = getSimpleDateInSeattle();
    try {
      await triggerRollover();
      if (this._todayPollGeneration !== generation) return;
      const result = await this._fetchPuzzleForDate(today, { cacheBust: true });
      if (this._todayPollGeneration !== generation) return;
      if (result.result === "Success") {
        this._mergeRemotePuzzleAndRebuild(result.puzzle);
        this.todayPuzzleStatus.value = "idle";
        return;
      }
    } catch (err) {
      console.error("Background rollover poll failed:", err);
      if (this._todayPollGeneration !== generation) return;
    }
    this._scheduleTodayPoll(playerId, generation);
  }

  /** Stop any in-flight/scheduled today poll (invalidates via the generation). */
  private _cancelTodayPolling(): void {
    this._todayPollGeneration++;
    if (this._todayPollTimer !== undefined) {
      clearTimeout(this._todayPollTimer);
      this._todayPollTimer = undefined;
    }
  }

  /**
   * Dates of finished past puzzles whose rare-word bonus hasn't been scored yet
   * (played, but rarityPoints is still unknown). These are the puzzles the
   * pending-score poll should chase. Today is excluded — its bonus is
   * intentionally withheld until tomorrow, so it isn't "pending".
   */
  private _getPendingScoreDates(): SimpleDate[] {
    if (this._session === undefined) return [];
    const today = getSimpleDateInSeattle();
    return this._session.puzzles
      .filter(
        (puzzle) =>
          !areSimpleDatesEqual(puzzle.date, today) &&
          puzzle.played &&
          (puzzle.rarityPoints.value === undefined || puzzle.rarityPoints.value === null),
      )
      .map((puzzle) => puzzle.date);
  }

  /**
   * Start the background poll for finished-but-unscored past puzzles if any
   * exist. Nudges the rollover immediately, then polls on a fixed cadence until
   * every pending puzzle is scored (or the cycle budget is exhausted).
   */
  private _startPendingScorePollingIfNeeded(): void {
    if (this._session === undefined) return;
    if (this._getPendingScoreDates().length === 0) return;

    this._cancelPendingScorePolling();
    const generation = this._pendingScorePollGeneration;
    const playerId = this._session.userGuid;

    // Poke the backend to run the rollover/scoring now, then poll for the result.
    void triggerRollover();
    this._schedulePendingScorePoll(playerId, generation, 0);
  }

  /** Schedule the next pending-score poll cycle (no-op if superseded/exhausted). */
  private _schedulePendingScorePoll(playerId: string, generation: number, cycle: number): void {
    if (this._pendingScorePollGeneration !== generation) return;
    if (cycle >= PENDING_SCORE_POLL_MAX_CYCLES) return;
    this._pendingScorePollTimer = setTimeout(() => {
      void this._runPendingScorePollCycle(playerId, generation, cycle);
    }, PENDING_SCORE_POLL_INTERVAL_MS);
  }

  /**
   * One pending-score poll: re-poke the rollover, cache-busted re-fetch of each
   * still-pending puzzle, and apply any newly-available bonus to the live puzzle
   * in place (so an open review screen updates without a reload). Reschedules
   * while anything remains pending.
   */
  private async _runPendingScorePollCycle(
    playerId: string,
    generation: number,
    cycle: number,
  ): Promise<void> {
    if (this._pendingScorePollGeneration !== generation) return;
    const pending = this._getPendingScoreDates();
    if (pending.length === 0) return; // everything scored — stop

    try {
      await triggerRollover();
      if (this._pendingScorePollGeneration !== generation) return;
      const results = await Promise.all(
        pending.map((date) => this._fetchPuzzleForDate(date, { cacheBust: true })),
      );
      if (this._pendingScorePollGeneration !== generation) return;
      for (const result of results) {
        if (result.result === "Success") {
          this._applyScoredDataToLivePuzzle(result.puzzle);
        }
      }
    } catch (err) {
      console.error("Pending-score poll failed:", err);
      if (this._pendingScorePollGeneration !== generation) return;
    }

    if (this._getPendingScoreDates().length > 0) {
      this._schedulePendingScorePoll(playerId, generation, cycle + 1);
    }
  }

  /**
   * Fill in a freshly-scored rare-word bonus on the *live* puzzle instance so a
   * currently-open review screen updates through its observables. Only applies
   * when the remote now carries a rarity score, and never overwrites an
   * already-known bonus (mirrors the merge's no-clobber rule). Persists so the
   * value survives a reload.
   */
  private _applyScoredDataToLivePuzzle(remote: IPuzzleRemoteDataV1): void {
    if (this._session === undefined) return;
    if (remote.rarityPoints === undefined || remote.rarityPoints === null) return;

    const live = this._session.puzzles.find((puzzle) =>
      areSimpleDatesEqual(puzzle.date, remote.date),
    );
    if (live === undefined) return;
    if (live.rarityPoints.value !== undefined && live.rarityPoints.value !== null) return;

    live.rarityPoints.value = remote.rarityPoints;
    live.totalScore.value =
      remote.totalPoints ?? (live.regularPoints.value ?? 0) + remote.rarityPoints;
    this._saveLocalSessionData(this._session);
  }

  /** Stop any in-flight/scheduled pending-score poll (invalidates via generation). */
  private _cancelPendingScorePolling(): void {
    this._pendingScorePollGeneration++;
    if (this._pendingScorePollTimer !== undefined) {
      clearTimeout(this._pendingScorePollTimer);
      this._pendingScorePollTimer = undefined;
    }
  }

  /**
   * Re-attempt fetching today's puzzle immediately (used by the "check again"
   * action on the "not ready yet" card, bypassing the poll interval). No-ops if
   * a load hasn't happened yet.
   */
  public retryTodaysPuzzle(): void {
    if (this._session === undefined) return;
    this.todayPuzzleStatus.value = "loading";
    void this._pokeForTodaysPuzzleInBackground(this._session.userGuid);
  }

  /**
   * Merge a freshly-fetched remote puzzle into the live session and rebuild it
   * so the puzzles getter (and thus the Home screen) reflects the new puzzle.
   */
  private _mergeRemotePuzzleAndRebuild(remotePuzzle: IPuzzleRemoteDataV1): void {
    if (this._session === undefined) return;
    const sessionData = this._session.serialize();
    this._mergeRemotePuzzleIntoSession(sessionData, remotePuzzle);
    sessionData.puzzles.sort((a: IPuzzleRemoteDataV1, b: IPuzzleRemoteDataV1) =>
      compareSimpleDates(a.date, b.date),
    );
    this._session = new SessionV1(sessionData);
    this._saveLocalSessionData(this._session);
  }

  private _mergeRemotePuzzleIntoSession(
    session: ISessionDataV1,
    remotePuzzle: IPuzzleRemoteDataV1,
  ): void {
    const existingIndex = session.puzzles.findIndex((candidate: IPuzzleRemoteDataV1) =>
      areSimpleDatesEqual(candidate.date, remotePuzzle.date),
    );

    if (existingIndex === -1) {
      session.puzzles.push({ ...remotePuzzle, timeElapsedInSeconds: 0 });
      return;
    }

    const existing = session.puzzles[existingIndex];
    const mergedWordsFound = Array.from(
      new Set([...existing.wordsFound, ...remotePuzzle.wordsFound]),
    );

    // "played" and "has been scored" are two different remote states:
    //  - `played` becomes true as soon as a raw play exists (pre-rollover), so
    //    once a player has played we keep it sticky and never regress.
    //  - Rare-word bonus / total are only known once the daily rollover has
    //    *scored* this player's play; `rarityPoints` (sourced solely from the
    //    scoredPlay) is the signal for that. A pre-rollover raw play reports
    //    played=true but carries no rarity yet, so guard the scored fields on
    //    rarityPoints being present and never clobber an already-known
    //    rarity/total with undefined (which would revert a finished puzzle's
    //    rare-word bonus back to a misleading 0).
    const played = remotePuzzle.played || existing.played;
    const remoteHasScoredData = remotePuzzle.rarityPoints !== undefined;
    const regularPoints = remotePuzzle.played ? remotePuzzle.regularPoints : existing.regularPoints;
    const rarityPoints = remoteHasScoredData ? remotePuzzle.rarityPoints : existing.rarityPoints;
    const totalPoints = remoteHasScoredData ? remotePuzzle.totalPoints : existing.totalPoints;

    session.puzzles[existingIndex] = {
      ...remotePuzzle,
      wordsFound: mergedWordsFound,
      played,
      regularPoints,
      rarityPoints,
      totalPoints,
      timeElapsedInSeconds: existing.timeElapsedInSeconds,
    };
  }

  private _saveLocalSessionData(session: ISessionV1): void {
    localStorage.setItem(LOCAL_DATA_KEY, JSON.stringify(session.serialize()));
  }

  /**
   * Only return cached data if it belongs to the currently-signed-in player.
   * This prevents leaking data from one Google account to another on shared devices.
   */
  private _loadLocalSessionData(playerId: string): ISessionDataV1 | undefined {
    const serializedData = localStorage.getItem(LOCAL_DATA_KEY);
    if (serializedData === null) return undefined;

    try {
      const dataObject = JSON.parse(serializedData) as ISessionData;
      if (dataObject.version !== "1") {
        console.warn(`Unknown session data version: ${dataObject.version}`);
        return undefined;
      }
      const typed = dataObject as ISessionDataV1;
      if (typed.userGuid !== playerId) {
        console.log("Cached session is for a different player; ignoring.");
        return undefined;
      }
      return typed;
    } catch (err) {
      console.error("Failed to parse local session data:", err);
      return undefined;
    }
  }

  private _createNewSessionData(playerId: string): ISessionDataV1 {
    return {
      version: "1",
      userGuid: playerId,
      puzzles: [],
    };
  }
}

export const DataLayerContext = createContext<DataLayer>(new DataLayer());
